import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import {
  BookOpen,
  MessageSquare,
  Plus,
  Sun,
  Moon,
  Trash2,
  Loader2,
  Monitor,
  X,
  SidebarClose,
  SidebarOpen,
  PanelTopClose,
  PanelTopOpen,
  LogOut,
  Settings,
  ChevronRight,
  ChevronDown,
  Check,
  Palette,
  UserRound,
  ExternalLink,
  WandSparkles,
  Library,
  History,
  BookMarked,
  Target,
  Download,
  Compass,
  MoreHorizontal,
  PenLine,
  Upload
} from 'lucide-react'
import SettingsPanel, { type SettingsSection } from './components/SettingsPanel'
import WikiPanel from './components/WikiPanel'
import ConversationView from './components/ConversationView'
import StoryCanvas from './components/StoryCanvas'
import UsageQuotaBanner from './components/UsageQuotaBanner'
import ChapterHistoryDialog from './components/ChapterHistoryDialog'
import StoryOnboarding from './components/StoryOnboarding'
import { GENRE_SEARCH_SOURCE, genreTokens, type GenreItem } from './lib/genres'
import WizardOfferModal from './components/WizardOfferModal'
import {
  isStoryUntouched,
  latestRecentConversation,
  latestUnreadConversation,
  shouldOfferWizard
} from './lib/storyStart'
import ChapterSidebar from './components/ChapterSidebar'
import SettingsSectionNav from './components/SettingsSectionNav'
import StoryBlurbField, { MIN_STORY_BLURB_LENGTH } from './components/StoryBlurbField'
import PublishStoryDialog from './components/PublishStoryDialog'
import { useFeatureFlags } from './hooks/useFeatureFlags'
import { AppShell } from '@astryxdesign/core/AppShell'
import { MobileNav } from '@astryxdesign/core/MobileNav'
import {
  SideNav,
  SideNavItem,
  SideNavRenderContext,
  SideNavSection
} from '@astryxdesign/core/SideNav'
import {
  Layout,
  LayoutContent,
  LayoutFooter,
  LayoutPanel,
  HStack,
  VStack
} from '@astryxdesign/core/Layout'
import PillButton from './components/PillButton'
import { IconButton } from '@astryxdesign/core/IconButton'
import { TextInput } from '@astryxdesign/core/TextInput'
import { NumberInput } from '@astryxdesign/core/NumberInput'
import { Tokenizer } from '@astryxdesign/core/Tokenizer'
import { List, ListItem } from '@astryxdesign/core/List'
import { Dialog, DialogHeader } from '@astryxdesign/core/Dialog'
import { DropdownMenu } from '@astryxdesign/core/DropdownMenu'
import { Popover } from '@astryxdesign/core/Popover'
import { Divider } from '@astryxdesign/core/Divider'
import { Card } from '@astryxdesign/core/Card'
import { Avatar } from '@astryxdesign/core/Avatar'
import { DEFAULT_AVATAR_IMAGE } from './lib/defaultAvatarImage'
import { Spinner } from '@astryxdesign/core/Spinner'
import { Text } from '@astryxdesign/core/Text'
import { ClickableCard } from '@astryxdesign/core/ClickableCard'
import { Token } from '@astryxdesign/core/Token'
import { useToast } from '@astryxdesign/core/Toast'
import Wordmark from './components/Wordmark'
import { authClient } from './lib/auth-client'
import { createId } from './lib/id'
import { getFullStoryText } from './lib/storyContent'
import { formatLocalDate, MAX_DAILY_GOAL, MIN_DAILY_GOAL } from './lib/writingDays'
import { useIsHandheld, useIsMobile, useMediaQuery } from './lib/useMediaQuery'
import { useManuscriptUndo } from './hooks/useManuscriptUndo'
import AuthView from './components/AuthView'
import LandingView from './components/LandingView'
import {
  startEditorTour,
  shouldAutoStartTour,
  waitFor,
  MANUSCRIPT_SELECTOR,
  type TourDeps,
  type TourControls
} from './lib/editorTour'
import PublishedStoryView from './components/PublishedStoryView'
import PrivacyPolicyView from './components/PrivacyPolicyView'
import TermsOfUseView from './components/TermsOfUseView'
import AllStoriesView from './components/AllStoriesView'
import AuthorProfileView from './components/AuthorProfileView'
import HomeView from './components/HomeView'
import LibraryView from './components/LibraryView'
import posthog from 'posthog-js'

export interface AppSettings {
  lmStudioUrl: string
  selectedModel: string
  apiKey?: string
  theme: 'light' | 'dark' | 'system'
  temperature?: number
  systemPromptTemplate?: string
  lastOpenedStoryId?: string
  activeProfileId?: string
  /** Undefined until the server responds — the tour trigger treats that as "not loaded yet". */
  hasSeenEditorTour?: boolean
}

interface StoryMetadata {
  id: string
  name: string
  createdAt: number
  updatedAt: number
  genre?: string
  summary?: string
  tone?: string
  perspective?: string
  setting?: string
  onboarded?: boolean
  dailyWordGoal?: number
}

interface Message {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: number
  metadata?: {
    actions?: AgentAction[]
    selection?: { text: string; instructions: string }
    guideItemIds?: string[]
    mode?: 'plan' | 'ask' | 'writing' | 'edit'
    plan?: { summary: string; content: string; comments?: PlanComment[] }
    timing?: AgentTiming
  }
}

interface PlanComment {
  id: string
  lineIndex: number
  lineText: string
  comment: string
}

interface Conversation {
  id: string
  name: string
  mode: 'plan' | 'ask' | 'writing'
  messages: Message[]
}

interface StoryData {
  metadata: StoryMetadata
  chapters: StoryChapter[]
  conversations: Conversation[]
  wiki: WikiPage[]
  paragraphNotes: ParagraphNote[]
}

interface PromptState {
  show: boolean
  title: string
  placeholder: string
  value: string
  onSubmit: (val: string) => void
}

interface ConfirmState {
  show: boolean
  title: string
  message: string
  onConfirm: () => void
  confirmText?: string
  cancelText?: string
  isAlertOnly?: boolean
  confirmVariant?: 'primary' | 'destructive'
}

type WorkspaceView = 'canvas' | 'wiki'

interface WorkspaceRoute {
  storyId: string
  view: WorkspaceView
  wikiPageId: string | null
  chapterNumber: number | null
}

interface PublicStoryRoute {
  publicationId: string
  slug: string
  version?: number
}

const parsePublicStoryRoute = (pathname = window.location.pathname): PublicStoryRoute | null => {
  const match = pathname.match(/^\/stories\/([^/]+)\/([^/]+)(?:\/versions\/(\d+))?\/?$/)
  if (!match) return null
  try {
    return {
      publicationId: decodeURIComponent(match[1]),
      slug: decodeURIComponent(match[2]),
      version: match[3] ? Number.parseInt(match[3], 10) : undefined
    }
  } catch {
    return null
  }
}

interface AuthorProfileRoute {
  profileId: string
  tab: 'published' | 'library' | 'reviews'
}

const parseAuthorProfileRoute = (
  pathname = window.location.pathname,
  search = window.location.search
): AuthorProfileRoute | null => {
  const match = pathname.match(/^\/profiles\/([^/]+)\/?$/)
  if (!match) return null
  try {
    // The tab lives in the query string so a specific tab is linkable and survives a reload.
    const requestedTab = new URLSearchParams(search).get('tab')
    return {
      profileId: decodeURIComponent(match[1]),
      tab: requestedTab === 'library' || requestedTab === 'reviews' ? requestedTab : 'published'
    }
  } catch {
    return null
  }
}

const parseWorkspaceRoute = (pathname = window.location.pathname): WorkspaceRoute | null => {
  const match = pathname.match(/^\/(?:drafts|stories)\/([^/]+)\/(canvas|wiki)(?:\/([^/]+))?\/?$/)
  if (!match) return null

  try {
    const view = match[2] as WorkspaceView
    const routeSegment = match[3] ? decodeURIComponent(match[3]) : null
    const chapterNumber =
      view === 'canvas' && routeSegment && /^\d+$/.test(routeSegment)
        ? Number.parseInt(routeSegment, 10)
        : null
    return {
      storyId: decodeURIComponent(match[1]),
      view,
      wikiPageId: view === 'wiki' ? routeSegment : null,
      chapterNumber
    }
  } catch {
    return null
  }
}

const workspacePath = (
  storyId: string,
  view: WorkspaceView,
  wikiPageId?: string | null,
  chapterNumber?: number | null
): string =>
  `/drafts/${encodeURIComponent(storyId)}/${view}${
    view === 'wiki' && wikiPageId
      ? `/${encodeURIComponent(wikiPageId)}`
      : view === 'canvas' && chapterNumber && chapterNumber > 1
        ? `/${chapterNumber}`
        : ''
  }`

const sortedChapters = (chapters: StoryChapter[]): StoryChapter[] =>
  [...chapters].sort((a, b) => a.position - b.position)

const chapterNumberForId = (chapters: StoryChapter[], chapterId: string | null): number | null => {
  if (!chapterId) return null
  const index = sortedChapters(chapters).findIndex((chapter) => chapter.id === chapterId)
  return index >= 0 ? index + 1 : null
}

const chapterForNumber = (
  chapters: StoryChapter[],
  chapterNumber: number | null | undefined
): StoryChapter | null => {
  const orderedChapters = sortedChapters(chapters)
  if (orderedChapters.length === 0) return null
  if (!chapterNumber || chapterNumber < 1 || chapterNumber > orderedChapters.length) {
    return orderedChapters[0]
  }
  return orderedChapters[chapterNumber - 1]
}

const CANVAS_SAVE_FEEDBACK_DELAY_MS = 5000

type StorySettingsSection = 'general' | 'goal' | 'chapters' | 'export' | 'delete'

const STORY_SETTINGS_COPY: Record<StorySettingsSection, { title: string; subtitle: string }> = {
  general: {
    title: 'General',
    subtitle: 'Update the identifying context used throughout this story.'
  },
  goal: {
    title: 'Daily goal',
    subtitle: 'Set how many words you want to add to this story each day.'
  },
  chapters: { title: 'Chapters', subtitle: 'Edit, reorder, add, or remove chapters.' },
  export: {
    title: 'Export',
    subtitle: 'Download this story as a PDF, formatted for reading and printing.'
  },
  delete: {
    title: 'Delete story',
    subtitle: 'Permanently remove this story and all of its content.'
  }
}

const formatLastActive = (conversation: Conversation): string => {
  if (!conversation.messages || conversation.messages.length === 0) return 'Never'
  const lastMsg = conversation.messages[conversation.messages.length - 1]
  const date = new Date(lastMsg.timestamp)

  const now = new Date()
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

function WorkspaceApp(): React.JSX.Element {
  const showToast = useToast()
  const isMobile = useIsMobile()
  // AppShell folds the SideNav into its own drawer at its 'md' breakpoint, which is
  // (max-width: 768px) -- inclusive of 768, where useIsMobile's 767.98 is not. iPad portrait
  // is exactly 768, so anything that depends on the nav being on screen has to use the
  // shell's boundary rather than the layout one: the guided tour, whose stops point into that
  // nav, and the leading header button, which has no rail left to toggle once the drawer owns it.
  const isNavCollapsed = useMediaQuery('(max-width: 768px)')
  // Phone or tablet, decided by pointer type rather than width (see useIsHandheld). The
  // guided tour is the only consumer: it is a hover-era spotlight walk over chrome that a
  // touch device either does not show or cannot drive, so it is withheld entirely there --
  // both the auto-start and the account-menu entry point that offers it.
  const isHandheld = useIsHandheld()
  const conversationSaveTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const canvasSaveRequest = useRef(0)
  const canvasSaveToastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const openStoryRequest = useRef(0)
  // Mirrors activeStoryId so async work can tell whether the writer has since switched stories.
  // State captured in a closure is the story that was active when the work STARTED; this is the
  // story that is active now. See refreshWikiFromServer for why this matters.
  const activeStoryIdRef = useRef<string | null>(null)
  // The path this workspace was mounted on. Read once: init() resumes after awaits, by which time
  // handleOpenStory may already have rewritten the URL out from under it.
  const bootPathnameRef = useRef(window.location.pathname)
  const { data: session, isPending: isSessionPending } = authClient.useSession()
  const { isEnabled: isFeatureEnabled, isLoading: areFlagsLoading } = useFeatureFlags(
    session?.user?.id ?? null
  )
  const coverEnabled = isFeatureEnabled('story-image')

  // Global States
  const [stories, setStories] = useState<StoryMetadata[]>([])
  const [activeStoryId, setActiveStoryId] = useState<string | null>(null)
  const [activeStoryData, setActiveStoryData] = useState<StoryData | null>(null)
  const [activeChapterId, setActiveChapterId] = useState<string | null>(null)
  // The wiki_revision the currently-loaded wiki array was built from. Echoed back on wiki saves
  // so the server only honours the destructive full-array delete when the client's view is
  // still provably current (stories.wiki_revision hasn't moved since) -- otherwise it applies
  // the upserts but skips the delete, so nothing written behind this client's back (the
  // manuscript sync, or a resolved suggestion) can be destroyed by the client's next wiki edit.
  //
  // Set ONLY at genuine .load() sites below (including inside refreshWikiFromServer, itself just
  // a wrapped .load()), deliberately NOT from save responses. Fix round 1
  // (a timestamp-based version of this same mechanism) adopted the fresh value from every save
  // response, which seemed sound but wasn't: once a stale save's response is adopted as the new
  // base, the very next save from the same client -- still unaware of whatever it hasn't
  // fetched -- looks "current" again and its delete fires for real, deleting exactly the row the
  // previous save's skip had just protected. Empirically confirmed for the revision version of
  // this same idea too (see task-3-report.md, fix round 2) before writing this ref this way: a
  // page a background writer creates survives one stale client save, then is wrongly deleted by
  // a second one if the first save's returned revision was adopted in between. Never adopting
  // from a save response, and refreshing only via a genuine full read (.load()), matches the
  // reasoning that makes this mechanism sound in the first place.
  const wikiRevisionRef = useRef<number | undefined>(undefined)
  // Mirrors wikiRevisionRef.current in React state: handleUpdateChapterContent's save .then
  // compares a save response's wikiRevision against this value to notice a revision bump from
  // outside this client. Assigned only alongside wikiRevisionRef, at the same genuine .load()
  // sites -- see that ref's own declaration comment for why.
  const [wikiRevision, setWikiRevision] = useState(0)
  // Pending manuscript-sync suggestions for the active story, reviewed in WikiPanel (Task 10).
  const [wikiProposals, setWikiProposals] = useState<WikiProposal[]>([])

  // The background sync writes to the wiki with no open stream to announce it. Every story save
  // returns the story's current wiki revision; when it passes the one this client holds,
  // something changed behind our back and we refetch rather than keep editing a stale array.
  const refreshWikiFromServer = useCallback(async (storyId: string): Promise<void> => {
    // allSettled, not all: a transient failure of the proposals endpoint alone must not discard
    // a successful story reload (or vice versa) -- see Finding 3.
    const [loadedResult, proposalsResult] = await Promise.allSettled([
      window.api.stories.load(storyId),
      window.api.wiki.proposals(storyId)
    ])

    // Logged unconditionally, ahead of the activeStoryIdRef guard below: a genuine fetch failure
    // is a real diagnostic no matter which story is on screen by the time it surfaces, and the
    // writer having switched away from `storyId` in the meantime is not a reason to drop it
    // silently. Only *applying* a successful result is gated on still being on this story.
    if (loadedResult.status === 'rejected') {
      console.error('Failed to refresh wiki:', loadedResult.reason)
    }
    if (proposalsResult.status === 'rejected') {
      console.error('Failed to refresh wiki proposals:', proposalsResult.reason)
    }

    // The writer may have switched to a different story while these fetches were in flight.
    // wikiRevisionRef and wikiProposals are un-keyed globals (unlike setActiveStoryData below,
    // which is guarded by comparing story ids) -- applying either now would leave the ref
    // describing THIS story's revision while activeStoryData.wiki holds the OTHER, now-active
    // story's pages. That mismatch is worse than doing nothing: the next wiki save on the
    // now-active story would send this story's stale revision as its base, and the server's
    // delete gate would treat a genuinely current client as stale (skipping a delete that should
    // have run) or, on an unlucky coincidental match, treat a genuinely stale client as current
    // (running a delete that destroys a page this client never saw) -- see Finding 1. Drop the
    // whole result rather than applying part of it.
    if (activeStoryIdRef.current !== storyId) return

    if (loadedResult.status === 'fulfilled') {
      const loaded = loadedResult.value
      // Only a genuine reload may advance the base revision: it is a claim that the array we are
      // holding is complete as of that revision, and only a full fetch establishes that pairing.
      // Never adopt a revision from a save response -- see wikiRevisionRef's own comment.
      wikiRevisionRef.current = loaded.wikiRevision ?? 0
      setWikiRevision(loaded.wikiRevision ?? 0)
      // Belt and braces alongside the activeStoryIdRef check above: stays correct even if this
      // function is ever called for a story that is not the active one on purpose.
      setActiveStoryData((current) =>
        current && current.metadata.id === storyId ? { ...current, wiki: loaded.wiki } : current
      )
    }

    if (proposalsResult.status === 'fulfilled') {
      setWikiProposals(proposalsResult.value.proposals || [])
    }
  }, [])

  // Derived up here rather than beside the JSX because useManuscriptUndo must run above the
  // isSessionPending / isInitializingWiki early returns further down.
  const activeChapter =
    activeStoryData?.chapters.find((chapter) => chapter.id === activeChapterId) ??
    activeStoryData?.chapters[0] ??
    null
  const manuscriptUndo = useManuscriptUndo(activeChapter?.id ?? null, activeChapter?.content ?? '')
  // A plain `.filter(...)` inline at the StoryCanvas call site below would build a new array
  // (new identity) on every render of this component — and ParagraphNoteMargin's measurement
  // effect depends on `notes`, so every render would tear down and rebuild its ResizeObserver and
  // its `document.fonts.ready` subscription. This component re-renders on every pointermove while
  // the writer drags the resizable chat panel, so that churn would run roughly 60 times a second.
  const paragraphNotesForActiveChapter = useMemo(
    () =>
      activeStoryData?.paragraphNotes.filter((note) => note.chapterId === activeChapter?.id) ?? [],
    [activeStoryData?.paragraphNotes, activeChapter?.id]
  )

  // Onboarding & Plan Execution States
  const [isInitializingWiki, setIsInitializingWiki] = useState<boolean>(false)
  const [wikiInitStep, setWikiInitStep] = useState<string>('')
  // The wizard is a full-screen takeover over the workspace, reached only by accepting the
  // offer below — never the landing state for a story.
  const [isWizardOpen, setIsWizardOpen] = useState<boolean>(false)
  const [isWizardOfferOpen, setIsWizardOfferOpen] = useState<boolean>(false)

  // Navigation & Workspace States
  const [activeMiddleTab, setActiveMiddleTab] = useState<WorkspaceView>('canvas')
  const [activeWikiPageId, setActiveWikiPageId] = useState<string | null>(null)
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null)
  const [pendingAgentSend, setPendingAgentSend] = useState<{
    conversationId: string
    messageId: string
  } | null>(null)
  const [showRightPanel, setShowRightPanel] = useState<boolean>(false)
  const [showLeftSidebar, setShowLeftSidebar] = useState<boolean>(true)
  // A flag of its own rather than a second meaning for showLeftSidebar. That one decides whether
  // the SideNav subtree is built at all, and below the shell's breakpoint the subtree is what the
  // drawer and the shell's top bar are made from, so spending it on a phone takes the drawer down
  // along with the bar. Not persisted, matching showLeftSidebar: a reload brings the bar back.
  const [isPhoneHeaderCollapsed, setIsPhoneHeaderCollapsed] = useState<boolean>(false)
  const editorTourRan = useRef(false)
  // The tour has no lifecycle tie to React: startEditorTour's driver.js instance and its
  // capture-phase window keyup listener live entirely in a JS closure, torn down only via
  // the tour's own exit paths (Next-to-completion, Close click, Escape). None of those fire
  // if the workspace the tour is anchored to disappears out from under it instead -- see the
  // activeStoryData-keyed effect below, which covers exactly that.
  const activeTourControls = useRef<TourControls | null>(null)
  const [canvasSelection, setCanvasSelection] = useState<string>('')
  const [showStoryDetails, setShowStoryDetails] = useState(false)
  const [showChapterHistory, setShowChapterHistory] = useState(false)
  const [restoredContent, setRestoredContent] = useState<{ html: string; token: number } | null>(
    null
  )
  const [storySettingsSection, setStorySettingsSection] = useState<StorySettingsSection>('general')
  const [isExportingPdf, setIsExportingPdf] = useState(false)
  const [isSavingStoryDetails, setIsSavingStoryDetails] = useState(false)
  const [showDeleteStoryDialog, setShowDeleteStoryDialog] = useState(false)
  const [deleteStoryName, setDeleteStoryName] = useState('')
  const [isDeletingStory, setIsDeletingStory] = useState(false)
  const [publication, setPublication] = useState<PublicationStatus | null>(null)
  const [dailyProgress, setDailyProgress] = useState<{ day: string; words: number } | null>(null)
  const [showPublishDialog, setShowPublishDialog] = useState(false)
  const [publicationBlurb, setPublicationBlurb] = useState('')
  const [publicationVisibility, setPublicationVisibility] =
    useState<PublicationVisibility>('public')
  const [publicationReaders, setPublicationReaders] = useState<string[]>([])
  const [isGeneratingBlurb, setIsGeneratingBlurb] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)
  const [coverUrl, setCoverUrl] = useState<string | null>(null)
  const [isGeneratingCover, setIsGeneratingCover] = useState(false)
  const [coverError, setCoverError] = useState<string | null>(null)
  const [coverStyles, setCoverStyles] = useState<CoverStyle[]>([])
  const [coverStylesError, setCoverStylesError] = useState<string | null>(null)
  const [selectedCoverStyle, setSelectedCoverStyle] = useState<string | null>(null)
  const [coverNote, setCoverNote] = useState('')
  const [isSuggestingCoverPrompt, setIsSuggestingCoverPrompt] = useState(false)
  const [storyDetailsDraft, setStoryDetailsDraft] = useState<{
    name: string
    genres: GenreItem[]
    blurb: string
    dailyGoal: number
  }>({ name: '', genres: [], blurb: '', dailyGoal: MIN_DAILY_GOAL })
  const [showChapterHeadingDialog, setShowChapterHeadingDialog] = useState(false)
  const [returnToChapterSettings, setReturnToChapterSettings] = useState(false)
  const [chapterHeadingDraft, setChapterHeadingDraft] = useState<{
    chapterId: string
    title: string
    subtitle: string
  }>({ chapterId: '', title: '', subtitle: '' })
  const [showAuth, setShowAuth] = useState(false)

  useEffect(() => {
    const user = session?.user
    if (!user) return
    posthog.identify(user.id, {
      email: user.email,
      name: user.name
    })
    posthog.capture('user_authenticated', { authentication_state: 'active_session' })
  }, [session?.user])

  // Reset selection when navigating or switching stories/tabs
  useEffect(() => {
    setCanvasSelection('')
  }, [activeStoryId, activeMiddleTab])

  useEffect(() => {
    if (canvasSaveToastTimer.current) {
      clearTimeout(canvasSaveToastTimer.current)
      canvasSaveToastTimer.current = null
    }
    canvasSaveRequest.current += 1
  }, [activeStoryId])

  useEffect(() => {
    activeStoryIdRef.current = activeStoryId
  }, [activeStoryId])

  useEffect(() => {
    setIsWizardOpen(false)
  }, [activeStoryId])

  useEffect(
    () => () => {
      if (canvasSaveToastTimer.current) clearTimeout(canvasSaveToastTimer.current)
    },
    []
  )

  // Settings State
  const [settings, setSettings] = useState<AppSettings>({
    lmStudioUrl: 'http://localhost:1234/v1',
    selectedModel: '',
    apiKey: '',
    theme: 'system',
    temperature: 0.7,
    systemPromptTemplate:
      'You are an expert co-writer. Help the user outline, draft, and rewrite their stories. Keep story draft copy clearly separated from chat and outlines.'
  })
  const [authorProfile, setAuthorProfile] = useState<AuthorProfile | null>(null)

  const [showSettings, setShowSettings] = useState(false)
  const [settingsInitialSection, setSettingsInitialSection] = useState<SettingsSection>('account')
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false)
  const [isThemeMenuOpen, setIsThemeMenuOpen] = useState(false)
  const [insertTextTrigger, setInsertTextTrigger] = useState<{
    text: string
    mode: 'cursor' | 'append' | 'replace'
  } | null>(null)
  const [rightPanelWidth, setRightPanelWidth] = useState<number>(384) // Default w-96 is 384px
  const [isResizing, setIsResizing] = useState<boolean>(false)
  const [promptModal, setPromptModal] = useState<PromptState>({
    show: false,
    title: '',
    placeholder: '',
    value: '',
    onSubmit: () => {}
  })

  const [confirmModal, setConfirmModal] = useState<ConfirmState>({
    show: false,
    title: '',
    message: '',
    onConfirm: () => {}
  })

  const applyTheme = (theme: 'light' | 'dark' | 'system'): void => {
    const root = document.documentElement
    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      root.setAttribute('data-theme', systemTheme)
      root.style.colorScheme = systemTheme
      root.classList.toggle('dark', systemTheme === 'dark')
    } else {
      root.setAttribute('data-theme', theme)
      root.style.colorScheme = theme
      root.classList.toggle('dark', theme === 'dark')
    }
  }

  const handleOpenStory = async (
    id: string,
    settingsSnapshot: AppSettings = settings,
    routeOptions: {
      view?: WorkspaceView
      wikiPageId?: string | null
      chapterNumber?: number | null
      history?: 'push' | 'replace' | 'none'
    } = {}
  ): Promise<void> => {
    const requestNumber = ++openStoryRequest.current
    try {
      const today = formatLocalDate(new Date())
      const [data, publicationStatus, progress] = await Promise.all([
        window.api.stories.load(id),
        window.api.stories.publication(id),
        window.api.stories.dailyProgress(id, today).catch(() => ({ day: today, words: 0 }))
      ])
      if (openStoryRequest.current !== requestNumber) return
      const view = routeOptions.view ?? 'canvas'
      const requestedWikiPageId = routeOptions.wikiPageId ?? null
      const wikiPageId =
        view === 'wiki'
          ? requestedWikiPageId && data.wiki.some((item) => item.id === requestedWikiPageId)
            ? requestedWikiPageId
            : 'home'
          : null
      const requestedChapter = chapterForNumber(data.chapters, routeOptions.chapterNumber)
      const requestedChapterNumber = chapterNumberForId(data.chapters, requestedChapter?.id ?? null)
      // Assigned synchronously rather than waiting for the mirroring effect below. useEffect is a
      // passive effect flushed on a later task, so between this call and that flush the ref would
      // still name the previous story -- and an async continuation landing in that gap would pass
      // the activeStoryIdRef guard wrongly. Leading is safe here; lagging is not: a ref that names
      // the story the writer just moved TO correctly discards the story they moved FROM.
      activeStoryIdRef.current = id
      setActiveStoryId(id)
      wikiRevisionRef.current = data.wikiRevision
      setWikiRevision(data.wikiRevision ?? 0)
      // Fire-and-forget: proposals are supplementary to the story load above, so a slow or
      // failed fetch here shouldn't block or fail opening the story itself. Guarded the same way
      // as refreshWikiFromServer: if the writer has already switched to a different story by the
      // time this resolves, applying it would show story `id`'s suggestions while another story
      // is on screen (see Finding 2).
      void window.api.wiki
        .proposals(id)
        .then((result) => {
          if (activeStoryIdRef.current !== id) return
          setWikiProposals(result.proposals || [])
        })
        .catch(() => {
          if (activeStoryIdRef.current !== id) return
          setWikiProposals([])
        })
      setActiveChapterId(requestedChapter?.id ?? null)
      setPublication(publicationStatus)
      setPublicationVisibility(publicationStatus?.visibility ?? 'public')
      setPublicationReaders(publicationStatus?.readers ?? [])
      setDailyProgress({ day: progress.day, words: progress.words })
      setActiveMiddleTab(view)
      setActiveWikiPageId(wikiPageId)
      setPendingAgentSend(null)
      // Precedence for what the chat panel shows on open, evaluated in order:
      //   a. an unread reply beats everything -- the writer left mid-conversation and the
      //      agent said something back since. Selecting it and opening the panel is the
      //      same "come back to what's waiting for you" behavior as the active-run check
      //      below, just for a reply that already finished rather than one still streaming.
      //      The migration that introduces last_read_at backfills every pre-existing
      //      conversation to NOW() in the same transaction that adds the column (see
      //      src/server/index.ts), so a null lastReadAt only ever occurs on a conversation
      //      created after that migration ran -- and a brand-new conversation has no reply
      //      yet to be unread about. This branch only fires for a real new reply, not as a
      //      one-time replay of history.
      //   b. failing that, the conversation the writer was just in -- one with a user or
      //      assistant message inside the last six hours -- is reselected, so going home or
      //      hopping to another story and back lands on the chat they left rather than on
      //      a fresh empty one. Read replies only, since (a) already took the unread case.
      //      The panel opens on desktop; on mobile the conversation is selected but the
      //      panel stays closed, for the same screen-space reason as (d) below.
      //   c. failing that, a story nobody has started opens with the co-writer already
      //      showing: the manuscript is blank, so the chat is the only thing on screen with
      //      anything to offer. Story creation always attaches an empty conversation, so
      //      there is one to select here and nothing has to be written to open it.
      //   d. failing that, on desktop only, open the panel onto the new-conversation view --
      //      nothing is unread, but leaving the panel closed on a wide screen wastes the
      //      space. Mobile keeps the panel closed here; screen space is scarce and a closed
      //      panel is less intrusive than one covering the manuscript on every open.
      //   e. otherwise, the existing closed-panel behavior.
      const unread = latestUnreadConversation(data.conversations || [])
      const recent = latestRecentConversation(data.conversations || [])
      const untouched = isStoryUntouched(data)
      const firstConversation = data.conversations[0]
      if (unread) {
        setActiveConversationId(unread.id)
        setShowRightPanel(true)
      } else if (recent) {
        setActiveConversationId(recent.id)
        setShowRightPanel(!isMobile)
      } else if (untouched && firstConversation) {
        setActiveConversationId(firstConversation.id)
        setShowRightPanel(true)
      } else if (!isMobile) {
        const emptyConversation = [...(data.conversations || [])]
          .reverse()
          .find(
            (conversation) =>
              !(conversation.messages || []).some(
                (message) => message.role === 'user' || message.role === 'assistant'
              )
          )
        if (emptyConversation) {
          setActiveConversationId(emptyConversation.id)
        } else {
          const newChat: Conversation = {
            id: createId(),
            name: 'New Chat',
            mode: 'writing',
            messages: []
          }
          data.conversations = [...(data.conversations || []), newChat]
          setActiveConversationId(newChat.id)
          // Every other conversation-creation site (handleCreateConversation,
          // handleStartRewriteDiscussion) persists the new row via handleSaveActiveStory
          // before anything else can reference it. That helper isn't safe to call here: it
          // reads `activeStoryId` off component state, which was just set a few lines above
          // but has not committed yet this tick, so calling it synchronously would still
          // close over the previous story's id (or null, on first load). Persist directly
          // against the same story-save route instead, keyed off the freshly loaded `id`
          // and `data` rather than closure state -- the narrowest payload that creates the
          // conversation row (conversations only; chapters/wiki/paragraphNotes stay
          // undefined so the server leaves them untouched). Fire-and-forget: opening the
          // story must not block on it, and a failure here just means the /agent route's
          // own self-heal INSERT (or the next real save) creates the row instead.
          void window.api.stories
            .save(id, { conversations: data.conversations })
            .catch((err) => console.error('Failed to persist the new chat conversation:', err))
        }
        setShowRightPanel(true)
      } else {
        setActiveConversationId(null) // Reset active conversation
        setShowRightPanel(false) // Hide chat panel
      }
      // data.paragraphNotes ?? []: tolerates a story saved before this feature existed. Fired
      // after the precedence block above so an in-memory new-conversation appended to
      // data.conversations in branch (d) is included in what actually renders.
      setActiveStoryData({ ...data, paragraphNotes: data.paragraphNotes ?? [] })
      // A story with a run still writing in the background reopens straight onto that
      // conversation, panel showing, so the writer sees the work in progress instead of a
      // closed panel. Not awaited: story load should not block on this. The membership guard
      // covers a registry entry whose conversation was deleted meanwhile.
      void window.api.agent
        .activeRun(id)
        .then(({ conversationId }) => {
          if (conversationId && (data.conversations || []).some((c) => c.id === conversationId)) {
            setActiveConversationId(conversationId)
            setShowRightPanel(true)
          }
        })
        .catch(() => {})
      // Re-evaluated on every open rather than remembered: coming back to a story that
      // still has not got going is exactly when the offer is worth making again. Suppressed
      // when the tour is about to auto-run for this story (hasSeenEditorTour === false):
      // the tour takes precedence, and its own last stop opens this same dialog, so opening
      // it here first would just flash it before the tour yanks focus away. Read off
      // settingsSnapshot rather than the `settings` state closure -- on the initial-mount
      // path this function is called with freshly-loaded settings before the setSettings
      // that would otherwise update that closure has committed, so `settings` here can
      // still be the stale pre-load default. hasSeenEditorTour undefined (settings not
      // resolved yet) does NOT suppress: the tour can't start in that state either, so the
      // offer still opens, and the auto-start effect below closes it defensively if
      // settings resolve afterwards to hasSeenEditorTour === false.
      setIsWizardOfferOpen(settingsSnapshot.hasSeenEditorTour !== false && shouldOfferWizard(data))

      if (routeOptions.history !== 'none') {
        const path = workspacePath(id, view, wikiPageId, requestedChapterNumber)
        if (routeOptions.history === 'replace') window.history.replaceState(null, '', path)
        else if (window.location.pathname !== path) window.history.pushState(null, '', path)
      }

      // Save last opened story in settings
      const updatedSettings = { ...settingsSnapshot, lastOpenedStoryId: id }
      setSettings(updatedSettings)
      await window.api.settings.save(updatedSettings)
    } catch (err) {
      if (openStoryRequest.current !== requestNumber) return
      console.error(`Failed to load story ${id}:`, err)
    }
  }

  // Load Settings and Stories on Mount/Session change
  useEffect(() => {
    if (!session?.user) {
      setStories([])
      // Synchronous ref assignment -- see the comment on this same pattern in handleOpenStory.
      activeStoryIdRef.current = null
      setActiveStoryId(null)
      setActiveStoryData(null)
      return
    }

    async function init(): Promise<void> {
      try {
        const [loadedSettings, loadedProfile] = await Promise.all([
          window.api.settings.get(),
          window.api.profiles.getActive()
        ])
        setAuthorProfile(loadedProfile)
        if (loadedSettings) {
          setSettings(loadedSettings)
          localStorage.setItem('ninenights-settings', JSON.stringify(loadedSettings))
          applyTheme(loadedSettings.theme)
        }

        const storyList = await window.api.stories.list()
        setStories(storyList)

        const requestedRoute = parseWorkspaceRoute()

        if (requestedRoute && storyList.some((s) => s.id === requestedRoute.storyId)) {
          handleOpenStory(requestedRoute.storyId, loadedSettings || settings, {
            view: requestedRoute.view,
            wikiPageId: requestedRoute.wikiPageId,
            chapterNumber: requestedRoute.chapterNumber,
            history: 'replace'
          })
        } else if (bootPathnameRef.current === '/' || bootPathnameRef.current === '/home') {
          // Landing on home stays on home. Restoring the last story here is what made the home
          // page flash and then hand itself over to the editor. The writer gets into a story by
          // choosing one, which is a full navigation to /drafts/:id/canvas.
        } else if (
          loadedSettings?.lastOpenedStoryId &&
          storyList.some((s) => s.id === loadedSettings.lastOpenedStoryId)
        ) {
          handleOpenStory(loadedSettings.lastOpenedStoryId, loadedSettings, { history: 'replace' })
        } else if (storyList.length > 0) {
          handleOpenStory(storyList[0].id, loadedSettings || settings, { history: 'replace' })
        } else {
          try {
            const meta = await window.api.stories.create('Untitled Story')
            setStories([meta])
            await handleOpenStory(meta.id, loadedSettings || settings, { history: 'replace' })
          } catch (err) {
            console.error('Failed to auto-create first story:', err)
            // Synchronous ref assignment -- see the comment on this same pattern in handleOpenStory.
            activeStoryIdRef.current = null
            setActiveStoryId(null)
            setActiveStoryData(null)
          }
        }
      } catch (err) {
        console.error('Failed to initialize app:', err)
      }
    }
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id])

  useEffect(() => {
    if (!session?.user) return

    const handlePopState = (): void => {
      const route = parseWorkspaceRoute()
      if (!route) return
      if (window.location.pathname.startsWith('/stories/')) {
        window.history.replaceState(
          null,
          '',
          workspacePath(route.storyId, route.view, route.wikiPageId, route.chapterNumber)
        )
      }

      if (!stories.some((story) => story.id === route.storyId)) {
        if (activeStoryId) {
          window.history.replaceState(
            null,
            '',
            workspacePath(
              activeStoryId,
              activeMiddleTab,
              activeWikiPageId,
              chapterNumberForId(activeStoryData?.chapters ?? [], activeChapterId)
            )
          )
        }
        return
      }

      if (route.storyId !== activeStoryId) {
        void handleOpenStory(route.storyId, settings, {
          view: route.view,
          wikiPageId: route.wikiPageId,
          chapterNumber: route.chapterNumber,
          history: 'none'
        })
        return
      }

      const wikiPageId =
        route.view === 'wiki'
          ? route.wikiPageId && activeStoryData?.wiki.some((item) => item.id === route.wikiPageId)
            ? route.wikiPageId
            : 'home'
          : null
      const requestedChapter =
        route.view === 'canvas'
          ? chapterForNumber(activeStoryData?.chapters ?? [], route.chapterNumber)
          : null
      const requestedChapterNumber = chapterNumberForId(
        activeStoryData?.chapters ?? [],
        requestedChapter?.id ?? null
      )
      setActiveMiddleTab(route.view)
      setActiveWikiPageId(wikiPageId)
      if (route.view === 'canvas') setActiveChapterId(requestedChapter?.id ?? null)
      const canonicalPath = workspacePath(
        route.storyId,
        route.view,
        wikiPageId,
        requestedChapterNumber
      )
      if (window.location.pathname !== canonicalPath) {
        window.history.replaceState(null, '', canonicalPath)
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
    // handleOpenStory intentionally uses the latest values captured by this subscription.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeMiddleTab,
    activeChapterId,
    activeStoryData,
    activeStoryId,
    activeWikiPageId,
    session?.user,
    settings,
    stories
  ])

  useEffect(() => {
    if (!activeStoryId || !activeStoryData || activeMiddleTab !== 'canvas') return
    const route = parseWorkspaceRoute()
    if (!route || route.storyId !== activeStoryId || route.view !== 'canvas') return

    const chapterNumber = chapterNumberForId(activeStoryData.chapters, activeChapterId)
    const path = workspacePath(activeStoryId, 'canvas', null, chapterNumber)
    if (window.location.pathname !== path) window.history.replaceState(null, '', path)
  }, [activeChapterId, activeMiddleTab, activeStoryData, activeStoryId])

  // Marks the open conversation read once the writer has actually had a chance to see it:
  // debounced so a brief glance while flipping through conversations doesn't fire a request,
  // and re-run on message-count change so a reply that lands while the panel is already open
  // (streaming in) gets marked read too, not just the state at the moment the panel opened.
  // Optimistic: sets lastReadAt in local state immediately rather than waiting on a refetch,
  // so latestUnreadConversation agrees with what's on screen right away.
  const activeConversationForRead = activeStoryData?.conversations.find(
    (c) => c.id === activeConversationId
  )
  const activeConversationMessageCount = activeConversationForRead?.messages.length ?? 0
  useEffect(() => {
    if (!showRightPanel || !activeConversationId) return
    const conversation = activeStoryData?.conversations.find((c) => c.id === activeConversationId)
    if (!conversation) return
    const hasRealMessage = conversation.messages.some(
      (message) => message.role === 'user' || message.role === 'assistant'
    )
    if (!hasRealMessage) return

    const timer = setTimeout(() => {
      window.api.conversations
        .markRead(activeConversationId)
        .then(({ lastReadAt }) => {
          setActiveStoryData((current) => {
            if (!current) return current
            return {
              ...current,
              conversations: current.conversations.map((c) =>
                c.id === activeConversationId ? { ...c, lastReadAt } : c
              )
            }
          })
        })
        .catch((err) => console.error('Failed to mark conversation read:', err))
    }, 1000)
    return () => clearTimeout(timer)
  }, [showRightPanel, activeConversationId, activeConversationMessageCount])

  const navigateToWorkspace = (view: WorkspaceView, wikiPageId: string | null = null): void => {
    if (!activeStoryId) return
    const resolvedWikiPageId = view === 'wiki' ? wikiPageId || activeWikiPageId || 'home' : null
    setActiveMiddleTab(view)
    setActiveWikiPageId(resolvedWikiPageId)
    const chapterNumber =
      view === 'canvas'
        ? chapterNumberForId(activeStoryData?.chapters ?? [], activeChapterId)
        : null
    const path = workspacePath(activeStoryId, view, resolvedWikiPageId, chapterNumber)
    if (window.location.pathname !== path) window.history.pushState(null, '', path)
  }

  const closeWikiModal = (): void => {
    if (!activeStoryId) return
    setActiveMiddleTab('canvas')
    const chapterNumber = chapterNumberForId(activeStoryData?.chapters ?? [], activeChapterId)
    const path = workspacePath(activeStoryId, 'canvas', null, chapterNumber)
    if (window.location.pathname !== path) window.history.replaceState(null, '', path)
  }

  // Listen to system theme changes dynamically
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleSystemThemeChange = (): void => {
      if (settings.theme === 'system') {
        applyTheme('system')
      }
    }

    // Add event listener
    mediaQuery.addEventListener('change', handleSystemThemeChange)
    return () => mediaQuery.removeEventListener('change', handleSystemThemeChange)
  }, [settings.theme])

  // Resize Side Panel Handler
  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e: MouseEvent): void => {
      const newWidth = window.innerWidth - e.clientX
      const maxWidth = window.innerWidth * 0.5
      const minWidth = 280

      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setRightPanelWidth(newWidth)
      } else if (newWidth < minWidth) {
        setRightPanelWidth(minWidth)
      } else if (newWidth > maxWidth) {
        setRightPanelWidth(maxWidth)
      }
    }

    const handleMouseUp = (): void => {
      setIsResizing(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing])

  // Story Management
  const handleCreateStory = async (): Promise<void> => {
    try {
      const meta = await window.api.stories.create('Untitled Story')
      setStories((prev) => [meta, ...prev])
      await handleOpenStory(meta.id)
    } catch (err) {
      console.error('Failed to create story:', err)
    }
  }

  // baseWikiRevision: callers whose updatedData.wiki came from activeStoryData.wiki unchanged
  // should pass wikiRevisionRef.current, the same value handleUpdateWiki sends — otherwise the
  // server treats a missing revision as "current" and this save's destructive delete can remove
  // any wiki page written behind this client's back since the last load, the same hazard this
  // task closes for handleUpdateWiki. Required (not optional) so a future caller can't silently
  // omit it and reopen the hazard for that path with no compile error — pass
  // wikiRevisionRef.current explicitly even where it's expected to be undefined.
  //
  // Returns the full StorySaveResult (null on failure) rather than a boolean: this shape is kept
  // from fix round 1, where callers used it to adopt a fresh value out of the response. Fix
  // round 2 replaced the timestamp-based version of that adoption with revision-match, and this
  // helper deliberately does NOT adopt result.wikiRevision into wikiRevisionRef -- see the ref's
  // own declaration comment for why doing so reintroduces the exact hazard this mechanism
  // exists to close. Kept the return shape anyway per instruction; it costs nothing and stays
  // available if a future round needs more than success/failure from a save.
  const handleSaveActiveStory = async (
    updatedData: StoryData,
    baseWikiRevision: number | undefined,
    // `isManuscriptImport` marks a save that delivers text the writer already had, so the
    // server re-baselines the chapter without counting it toward the daily goal.
    options: { isManuscriptImport?: boolean } = {}
  ): Promise<StorySaveResult | null> => {
    if (!activeStoryId) return null
    try {
      setActiveStoryData(updatedData)
      const result = await window.api.stories.save(
        activeStoryId,
        { ...updatedData, ...options },
        baseWikiRevision
      )

      // Update story list metadata timestamp
      setStories((prev) =>
        prev
          .map((s) => (s.id === activeStoryId ? { ...s, updatedAt: Date.now() } : s))
          .sort((a, b) => b.updatedAt - a.updatedAt)
      )
      return result
    } catch (err) {
      console.error('Failed to save story data:', err)
      return null
    }
  }

  // Shared by handleOnboardingComplete and handlePasteStorySubmit: parses each item into
  // wiki-page candidates, merges duplicates by type+title, then expands the merged pages
  // into prose with a bounded worker pool. A single item's parse/expand failure degrades
  // that item to a fallback rather than aborting the whole batch.
  const buildWikiPages = async (
    storyId: string,
    parseItems: OnboardingParseItem[],
    context: OnboardingParseItem['context'],
    onProgress: (step: string) => void
  ): Promise<ParsedWikiPage[]> => {
    let completedItems = 0
    onProgress(`Parsing story details (0/${parseItems.length})...`)
    const parsedResults = await Promise.all(
      parseItems.map(async (item) => {
        try {
          return await window.api.stories.parseOnboardingItem(storyId, item)
        } catch (err) {
          console.warn(`Wiki extraction failed for "${item.label}":`, err)
          return { pages: [], usedFallback: true }
        } finally {
          completedItems += 1
          onProgress(`Parsing story details (${completedItems}/${parseItems.length})...`)
        }
      })
    )

    const summaryPages = Array.from(
      parsedResults
        .flatMap((result) => result.pages)
        .reduce((merged, page) => {
          const key = `${page.type}:${page.name.trim().toLowerCase()}`
          const existing = merged.get(key)
          if (!existing) merged.set(key, { ...page })
          else if (!existing.body.includes(page.body)) {
            existing.body = `${existing.body}\n\n${page.body}`
          }
          return merged
        }, new Map<string, ParsedWikiPage>())
        .values()
    )

    const expandedPages = new Array<ParsedWikiPage>(summaryPages.length)
    let nextPageIndex = 0
    let completedPages = 0
    const expandNextPage = async (): Promise<void> => {
      while (nextPageIndex < summaryPages.length) {
        const pageIndex = nextPageIndex
        nextPageIndex += 1
        try {
          const result = await window.api.stories.expandOnboardingPage(
            storyId,
            summaryPages[pageIndex],
            context
          )
          expandedPages[pageIndex] = result.page
        } catch (err) {
          console.warn(`Wiki prose expansion failed for ${summaryPages[pageIndex].name}:`, err)
          expandedPages[pageIndex] = summaryPages[pageIndex]
        } finally {
          completedPages += 1
          onProgress(`Polishing guide pages (${completedPages}/${summaryPages.length})...`)
        }
      }
    }

    if (summaryPages.length > 0) {
      onProgress(`Polishing guide pages (0/${summaryPages.length})...`)
      const workerCount = Math.min(4, summaryPages.length)
      await Promise.all(Array.from({ length: workerCount }, () => expandNextPage()))
    }

    return expandedPages
  }

  const handleOnboardingComplete = async (
    genres: string[],
    tones: string[],
    toneText: string,
    characterText: string,
    perspective: string,
    perspectiveText: string,
    setting: string,
    settingText: string,
    promptText: string
  ): Promise<boolean> => {
    if (!activeStoryData || !activeStoryId) return false

    setIsInitializingWiki(true)
    setWikiInitStep('Preparing story details...')

    try {
      const context = {
        genres,
        tones,
        perspective,
        setting,
        toneDetails: toneText.trim(),
        perspectiveDetails: perspectiveText.trim(),
        settingDetails: settingText.trim()
      }
      const parseItems: OnboardingParseItem[] = [
        ...(characterText.trim()
          ? [
              {
                sourceType: 'characters' as const,
                label: 'Characters',
                content: characterText.trim(),
                context
              }
            ]
          : []),
        ...(setting || settingText.trim()
          ? [
              {
                sourceType: 'setting' as const,
                label: setting || 'Primary Setting',
                content: settingText.trim() || setting,
                context
              }
            ]
          : []),
        ...(promptText.trim()
          ? [
              {
                sourceType: 'prompt' as const,
                label: 'Opening Story Idea',
                content: promptText.trim(),
                context
              }
            ]
          : [])
      ]

      const titleSource = [characterText, settingText, promptText]
        .map((value) => value.trim())
        .filter(Boolean)
        .join('\n\n')
      const [expandedPages, titleResult] = await Promise.all([
        buildWikiPages(activeStoryId, parseItems, context, setWikiInitStep),
        titleSource
          ? window.api.stories
              .suggestOnboardingTitle(activeStoryId, titleSource, context)
              .catch((err) => {
                console.warn('Story title suggestion failed:', err)
                return { title: null, usedFallback: true }
              })
          : Promise.resolve({ title: null, usedFallback: true })
      ])

      setWikiInitStep('Building your wiki...')
      await window.api.stories.initializeWiki(activeStoryId, {
        genres,
        tones,
        toneText,
        characterText,
        perspective,
        perspectiveText,
        setting,
        settingText,
        promptText,
        storyTitle: titleResult.title,
        pages: expandedPages
      })

      setWikiInitStep('Opening your story workspace...')

      // 2. Reload story data to populate everything
      const updatedData = await window.api.stories.load(activeStoryId)

      // 3. If promptText is provided, let's append it to the first conversation
      const finalStoryData = { ...updatedData }
      if (promptText && promptText.trim() !== '') {
        const firstConv = updatedData.conversations[0]
        if (firstConv) {
          const newUserMsg = {
            id: createId(),
            role: 'user' as const,
            content: promptText.trim(),
            timestamp: Date.now()
          }
          const updatedConversations = updatedData.conversations.map((c) =>
            c.id === firstConv.id ? { ...c, messages: [...c.messages, newUserMsg] } : c
          )
          finalStoryData.conversations = updatedConversations

          // Save it. updatedData.wikiRevision (not wikiRevisionRef, which the fresh-load effect
          // below hasn't updated yet at this point) is the correct base here: it's the revision
          // this just-loaded wiki array — still exactly what finalStoryData.wiki holds, since
          // only .conversations was changed above — was actually built from.
          await window.api.stories.save(activeStoryId, finalStoryData, updatedData.wikiRevision)
          setPendingAgentSend({ conversationId: firstConv.id, messageId: newUserMsg.id })
          setActiveConversationId(firstConv.id)
          setShowRightPanel(true)
        }
      } else {
        const firstConv = updatedData.conversations[0]
        if (firstConv) {
          setActiveConversationId(firstConv.id)
          setShowRightPanel(true)
        }
      }

      setActiveStoryData(finalStoryData)
      wikiRevisionRef.current = updatedData.wikiRevision
      setWikiRevision(updatedData.wikiRevision ?? 0)
      setStories((prev) =>
        prev.map((story) =>
          story.id === activeStoryId ? { ...story, ...updatedData.metadata } : story
        )
      )
      navigateToWorkspace('canvas')
      // Nothing else unmounts the wizard: it renders on its own flag now, not on the story
      // still being un-onboarded, so without this it would stay over the story it just built.
      setIsWizardOpen(false)
      posthog.capture('story_onboarding_completed', {
        genre_count: genres.length,
        tone_count: tones.length,
        has_prompt: promptText.trim().length > 0,
        wiki_page_count: expandedPages.length
      })
      return true
    } catch (err) {
      console.error('Wiki initialization failed:', err)
      showToast({
        body: "Something went wrong setting up your story. Your answers weren't lost — please try again.",
        type: 'error'
      })
      return false
    } finally {
      setIsInitializingWiki(false)
    }
  }

  // Active Story specific modifications
  const handleUpdateChapterContent = (chapterId: string, newContent: string): void => {
    if (!activeStoryData || !activeStoryId) return
    const storyId = activeStoryId
    const updatedChapters = activeStoryData.chapters.map((chapter) =>
      chapter.id === chapterId ? { ...chapter, content: newContent } : chapter
    )
    const updated = { ...activeStoryData, chapters: updatedChapters }
    setActiveStoryData(updated)
    const saveNumber = ++canvasSaveRequest.current
    let saveSettled = false
    let saveSucceeded = false
    const savePromise = window.api.stories
      .save(storyId, { chapters: updatedChapters })
      .then((result) => {
        // This save can resolve after the writer has already switched to a different
        // story (nothing aborts the in-flight request), or after a later keystroke on
        // this same story has already kicked off a newer save of its own. Unlike the
        // setStories update below, which is keyed by story.id, dailyProgress is a single
        // un-keyed value, so an unguarded set here would clobber the freshest word count
        // with a stale one. canvasSaveRequest.current is bumped both when activeStoryId
        // changes (see the effect above) and on every save issued here (see saveNumber
        // above), so comparing it against the saveNumber captured when this save started
        // means "this is still the most recently issued save, and we're still on its
        // story" — not just "same story".
        if (result.dailyWords !== null && canvasSaveRequest.current === saveNumber) {
          setDailyProgress({ day: result.day, words: result.dailyWords })
        }
        setStories((previous) =>
          previous
            .map((story) => (story.id === storyId ? { ...story, updatedAt: Date.now() } : story))
            .sort((left, right) => right.updatedAt - left.updatedAt)
        )
        // The background sync (or another tab) can bump the wiki behind this client's back with
        // no open stream to announce it. This save's response is the first place we'd learn that
        // happened, so treat a returned revision higher than the one we hold as the signal to
        // refetch -- see refreshWikiFromServer and wikiRevisionRef's own comment for why this
        // must be a refetch and never a direct assignment of the returned number.
        if (typeof result?.wikiRevision === 'number' && result.wikiRevision > wikiRevision) {
          void refreshWikiFromServer(storyId)
        }
        return true
      })
      .catch((err) => {
        console.error('Failed to save story content:', err)
        return false
      })
      .then((saved) => {
        saveSettled = true
        saveSucceeded = saved
        return saved
      })
    if (canvasSaveToastTimer.current) clearTimeout(canvasSaveToastTimer.current)
    canvasSaveToastTimer.current = setTimeout(() => {
      canvasSaveToastTimer.current = null
      if (canvasSaveRequest.current !== saveNumber) return

      const showSaveResult = (saved: boolean): void => {
        if (canvasSaveRequest.current !== saveNumber) return
        showToast({
          body: saved ? 'Story saved' : 'Story could not be saved',
          type: saved ? 'info' : 'error',
          uniqueID: 'story-canvas-save',
          collisionBehavior: 'overwrite',
          isAutoHide: saved,
          autoHideDuration: 1800
        })
      }

      if (saveSettled) {
        showSaveResult(saveSucceeded)
        return
      }

      showToast({
        body: 'Saving story…',
        uniqueID: 'story-canvas-save',
        collisionBehavior: 'overwrite',
        isAutoHide: false
      })
      void savePromise.then(showSaveResult)
    }, CANVAS_SAVE_FEEDBACK_DELAY_MS)
  }

  /**
   * Saves a note and the chapter HTML that anchors it in one request.
   *
   * Two separate writes would race: the chapter save is fire-and-forget on every keystroke with no
   * in-flight mutex, so an older save carrying pre-anchor HTML can land after this one and erase
   * the anchor while the note row commits fine. The server applies both inside one transaction.
   */
  const handleSaveParagraphNote = (
    mutation: ParagraphNoteMutation,
    chapterId: string,
    chapterHtml: string
  ): void => {
    if (!activeStoryData || !activeStoryId) return
    const storyId = activeStoryId
    const updatedChapters = activeStoryData.chapters.map((chapter) =>
      chapter.id === chapterId ? { ...chapter, content: chapterHtml } : chapter
    )
    const existing = activeStoryData.paragraphNotes.filter(
      (note) => note.anchorId !== mutation.anchorId
    )
    const optimistic: ParagraphNote[] = mutation.deleted
      ? existing
      : [
          ...existing,
          {
            id: `pending-${mutation.anchorId}`,
            chapterId,
            anchorId: mutation.anchorId,
            body: mutation.body ?? '',
            isAgentContext: mutation.isAgentContext !== false,
            anchorPosition: mutation.anchorPosition ?? 0,
            isOrphaned: false,
            isFootnote: mutation.isFootnote === true
          }
        ]
    const previousNotes = activeStoryData.paragraphNotes
    setActiveStoryData({
      ...activeStoryData,
      chapters: updatedChapters,
      paragraphNotes: optimistic
    })

    window.api.stories
      .save(storyId, { chapters: updatedChapters, paragraphNotes: [mutation] })
      .then(() => {
        // Fired only once the save actually lands, not optimistically: the metric this feeds is
        // adoption, and a save the .catch below had to roll back never happened as far as the
        // writer's usage is concerned. Deliberately NOT gated on activeStoryIdRef, unlike the
        // rollback below — that guard exists to keep React state consistent for whichever story
        // is on screen now, which doesn't apply here. This mutation, closed over above, either
        // succeeded or it didn't; the writer having since switched stories doesn't change that.
        const isExistingNote = previousNotes.some((note) => note.anchorId === mutation.anchorId)
        if (mutation.deleted) {
          posthog.capture('paragraph_note_deleted', {})
        } else if (isExistingNote) {
          posthog.capture('paragraph_note_updated', {
            body_length: (mutation.body ?? '').length,
            is_agent_context: mutation.isAgentContext !== false,
            is_footnote: mutation.isFootnote === true
          })
        } else {
          posthog.capture('paragraph_note_created', {
            body_length: (mutation.body ?? '').length,
            is_agent_context: mutation.isAgentContext !== false,
            is_footnote: mutation.isFootnote === true
          })
        }
      })
      .catch((err) => {
        console.error('Failed to save paragraph note:', err)
        // The writer may have switched stories before this rejection landed. Without this guard the
        // rollback would overwrite the CURRENT story's notes with the previous story's array, and
        // the toast would report a failure for a story that is no longer on screen. Same hazard
        // canvasSaveRequest guards on the chapter-save path.
        if (activeStoryIdRef.current !== storyId) return
        setActiveStoryData((current) =>
          current && current.metadata.id === storyId
            ? { ...current, paragraphNotes: previousNotes }
            : current
        )
        showToast({
          body: 'Note could not be saved',
          type: 'error',
          uniqueID: 'paragraph-note-save',
          collisionBehavior: 'overwrite',
          isAutoHide: true,
          autoHideDuration: 2400
        })
      })
  }

  const handleAddChapter = (): void => {
    if (!activeStoryData || !activeStoryId) return
    const storyId = activeStoryId
    const nextPosition = activeStoryData.chapters.length
    const newChapter: StoryChapter = {
      id: createId(),
      title: `Chapter ${nextPosition + 1}`,
      subtitle: '',
      position: nextPosition,
      content: ''
    }
    // The very first "+ New chapter" click also names the previously-lone chapter, since it's
    // about to become visible in a list for the first time.
    const renamedExisting = activeStoryData.chapters.map((chapter, index) =>
      index === 0 && !chapter.title.trim() && activeStoryData.chapters.length === 1
        ? { ...chapter, title: 'Chapter 1' }
        : chapter
    )
    const updatedChapters = [...renamedExisting, newChapter]
    setActiveStoryData({ ...activeStoryData, chapters: updatedChapters })
    setActiveChapterId(newChapter.id)
    setChapterHeadingDraft({
      chapterId: newChapter.id,
      title: newChapter.title,
      subtitle: newChapter.subtitle
    })
    window.api.stories
      .save(storyId, { chapters: updatedChapters })
      .catch((err) => console.error('Failed to save new chapter:', err))
  }

  const handleDeleteChapter = (chapterId: string): void => {
    if (!activeStoryData || !activeStoryId) return
    if (activeStoryData.chapters.length <= 1) return
    const chapter = activeStoryData.chapters.find((item) => item.id === chapterId)
    if (!chapter) return
    const storyId = activeStoryId
    const chapterLabel = chapter.title.trim() || `Chapter ${chapter.position + 1}`

    setConfirmModal({
      show: true,
      title: 'Delete Chapter',
      message: `Are you sure you want to delete “${chapterLabel}”? This action cannot be undone.`,
      onConfirm: () => {
        const remaining = activeStoryData.chapters
          .filter((item) => item.id !== chapterId)
          .map((item, index) => ({ ...item, position: index }))
        setActiveStoryData({ ...activeStoryData, chapters: remaining })
        manuscriptUndo.forgetChapter(chapterId)
        if (activeChapterId === chapterId) setActiveChapterId(remaining[0]?.id ?? null)
        window.api.stories
          .save(storyId, { chapters: remaining })
          .catch((err) => console.error('Failed to delete chapter:', err))
      }
    })
  }

  const handleMoveChapter = (chapterId: string, direction: 'up' | 'down'): void => {
    if (!activeStoryData || !activeStoryId) return
    const storyId = activeStoryId
    const sorted = [...activeStoryData.chapters].sort((a, b) => a.position - b.position)
    const index = sorted.findIndex((chapter) => chapter.id === chapterId)
    const swapWith = direction === 'up' ? index - 1 : index + 1
    if (index === -1 || swapWith < 0 || swapWith >= sorted.length) return
    ;[sorted[index], sorted[swapWith]] = [sorted[swapWith], sorted[index]]
    const reindexed = sorted.map((chapter, i) => ({ ...chapter, position: i }))
    setActiveStoryData({ ...activeStoryData, chapters: reindexed })
    window.api.stories
      .save(storyId, { chapters: reindexed })
      .catch((err) => console.error('Failed to reorder chapters:', err))
  }

  const handleRenameChapter = (chapterId: string, title: string, subtitle: string): void => {
    if (!activeStoryData || !activeStoryId) return
    const storyId = activeStoryId
    const updatedChapters = activeStoryData.chapters.map((chapter) =>
      chapter.id === chapterId ? { ...chapter, title, subtitle } : chapter
    )
    setActiveStoryData({ ...activeStoryData, chapters: updatedChapters })
    window.api.stories
      .save(storyId, { chapters: updatedChapters })
      .catch((err) => console.error('Failed to rename chapter:', err))
  }

  const handleMergeChapters = (): void => {
    if (!activeStoryData || !activeStoryId) return
    if (activeStoryData.chapters.length <= 1) return
    const storyId = activeStoryId
    const sorted = [...activeStoryData.chapters].sort((a, b) => a.position - b.position)
    const merged: StoryChapter = {
      ...sorted[0],
      title: '',
      subtitle: '',
      position: 0,
      // Joined with nothing between them: each chapter's content ends in a block, so the seam
      // already breaks the line. Under the manuscript's pre-wrap a newline here would be two
      // blank lines at every merge point.
      content: sorted.map((chapter) => chapter.content).join('')
    }
    // merged keeps sorted[0]'s id but carries every chapter's prose. Without this reset the
    // cached stack still holds the pre-merge text, and a single Cmd+Z after the next keystroke
    // would restore it — destroying the merged-in chapters, which no longer exist as rows.
    manuscriptUndo.resetChapter(merged.id, merged.content)
    // The other chapters' ids can never become active again, and each cached entry holds up to
    // 200 full chapter snapshots rather than deltas — worth reclaiming rather than leaving to
    // sit for the life of the session.
    sorted.slice(1).forEach((chapter) => manuscriptUndo.forgetChapter(chapter.id))
    setActiveStoryData({ ...activeStoryData, chapters: [merged] })
    setActiveChapterId(merged.id)
    window.api.stories
      .save(storyId, { chapters: [merged] })
      .catch((err) => console.error('Failed to merge chapters:', err))
  }

  const handleUpdateWiki = (updatedWiki: WikiPage[]): void => {
    if (!activeStoryData || !activeStoryId) return
    const storyId = activeStoryId
    const updated = {
      ...activeStoryData,
      wiki: updatedWiki
    }
    setActiveStoryData(updated)
    // wikiRevisionRef.current is deliberately not refreshed from this save's response -- see the
    // ref's own declaration comment for why that would reopen the hazard this task closes.
    const baseRevision = wikiRevisionRef.current
    void window.api.stories
      .save(storyId, { wiki: updatedWiki }, baseRevision)
      .then((result) => {
        // Still never adopt result.wikiRevision into the ref -- see the ref's own comment above.
        // A higher revision means something wrote behind our back, so REFETCH, which updates the
        // ref and the array together. Without this the ref never advances on the wiki-editing
        // path at all, and every later delete is silently skipped by the server's gate while the
        // UI reports success.
        if (
          typeof result?.wikiRevision === 'number' &&
          (baseRevision === undefined || result.wikiRevision > baseRevision)
        ) {
          void refreshWikiFromServer(storyId)
        }
      })
      .catch((err) => {
        console.error('Failed to save story wiki:', err)
      })
  }

  const handleResolveProposal = async (
    proposalId: string,
    action: 'accept' | 'reject'
  ): Promise<boolean> => {
    if (!activeStoryId) return false
    setWikiProposals((current) => current.filter((proposal) => proposal.id !== proposalId))
    try {
      await window.api.wiki.resolveProposal(activeStoryId, proposalId, action)
      await refreshWikiFromServer(activeStoryId)
      return true
    } catch (err) {
      console.error('Failed to resolve wiki suggestion:', err)
      void refreshWikiFromServer(activeStoryId)
      return false
    }
  }

  const handleSyncWikiNow = async (): Promise<void> => {
    if (!activeStoryId) return
    try {
      await window.api.wiki.syncNow(activeStoryId)
    } catch (err) {
      console.error('Failed to start wiki sync:', err)
    }
  }

  // Chat/Conversation management inside active story
  const handleCreateConversation = async (): Promise<void> => {
    if (!activeStoryData) return
    setShowRightPanel(true)
    const emptyConversation = [...(activeStoryData.conversations || [])]
      .reverse()
      .find(
        (conversation) =>
          !conversation.messages.some(
            (message) => message.role === 'user' || message.role === 'assistant'
          )
      )
    if (emptyConversation) {
      setActiveConversationId(emptyConversation.id)
      return
    }

    // The system prompt isn't added yet — it's inserted once the writer
    // sends their first message in this conversation.
    const newChat: Conversation = {
      id: createId(),
      name: 'New Chat',
      mode: 'writing',
      messages: []
    }
    const updated = {
      ...activeStoryData,
      conversations: [...(activeStoryData.conversations || []), newChat]
    }
    let saveResult = await handleSaveActiveStory(updated, wikiRevisionRef.current)
    if (!saveResult) {
      saveResult = await handleSaveActiveStory(updated, wikiRevisionRef.current)
    }
    setActiveConversationId(newChat.id)
    if (!saveResult) {
      showToast({
        body: "Couldn't save the new chat — check your connection. Sending a message may fail until it's saved."
      })
    }
  }

  const handleUpdateMessages = (conversationId: string, messages: Message[]): void => {
    if (!activeStoryId) return
    setActiveStoryData((current) => {
      if (!current) return current
      const target = (current.conversations || []).find(
        (conversation) => conversation.id === conversationId
      )
      const incomingIds = new Set(messages.map((message) => message.id))
      const newestIncomingTimestamp = messages.reduce(
        (latest, message) => Math.max(latest, message.timestamp || 0),
        0
      )
      const newerMessages =
        target?.messages.filter(
          (message) => !incomingIds.has(message.id) && message.timestamp > newestIncomingTimestamp
        ) || []
      const mergedMessages = [...messages, ...newerMessages].sort(
        (left, right) => left.timestamp - right.timestamp
      )
      const conversations = (current.conversations || []).map((conversation) =>
        conversation.id === conversationId
          ? { ...conversation, messages: mergedMessages }
          : conversation
      )
      const existingTimer = conversationSaveTimers.current.get(conversationId)
      if (existingTimer) clearTimeout(existingTimer)
      conversationSaveTimers.current.set(
        conversationId,
        setTimeout(() => {
          conversationSaveTimers.current.delete(conversationId)
          void window.api.conversations
            .saveMessages(conversationId, mergedMessages)
            .catch((err) => {
              console.error('Failed to save conversation:', err)
            })
        }, 250)
      )
      return { ...current, conversations }
    })
  }

  const handleSetConversationMode = (
    conversationId: string,
    mode: 'plan' | 'ask' | 'writing'
  ): void => {
    setActiveStoryData((current) => {
      if (!current) return current
      const conversations = (current.conversations || []).map((conversation) =>
        conversation.id === conversationId ? { ...conversation, mode } : conversation
      )
      return { ...current, conversations }
    })
    void window.api.conversations.setMode(conversationId, mode).catch((err) => {
      console.error('Failed to save conversation mode:', err)
    })
  }

  const handleRenameConversation = (conversationId: string, value: string): void => {
    const name = value.trim()
    if (!name || name.length > 80) {
      showToast({ body: 'Chat names must be between 1 and 80 characters.', type: 'error' })
      return
    }
    const previousName = activeStoryData?.conversations.find(
      (conversation) => conversation.id === conversationId
    )?.name
    if (!previousName || previousName === name) return

    setActiveStoryData((current) => {
      if (!current) return current
      return {
        ...current,
        conversations: current.conversations.map((conversation) =>
          conversation.id === conversationId ? { ...conversation, name } : conversation
        )
      }
    })
    void window.api.conversations.rename(conversationId, name).catch((err) => {
      console.error('Failed to rename conversation:', err)
      setActiveStoryData((current) => {
        if (!current) return current
        return {
          ...current,
          conversations: current.conversations.map((conversation) =>
            conversation.id === conversationId && conversation.name === name
              ? { ...conversation, name: previousName }
              : conversation
          )
        }
      })
      showToast({ body: "Couldn't rename this chat — check your connection.", type: 'error' })
    })
  }

  const openConversationRename = (conversation: Conversation): void => {
    setPromptModal({
      show: true,
      title: 'Rename Chat',
      placeholder: 'Enter a chat name',
      value: conversation.name,
      onSubmit: (value) => handleRenameConversation(conversation.id, value)
    })
  }

  const handleConversationTitleGenerated = (conversationId: string, title: string): void => {
    setActiveStoryData((current) => {
      if (!current) return current
      return {
        ...current,
        conversations: current.conversations.map((conversation) =>
          conversation.id === conversationId ? { ...conversation, name: title } : conversation
        )
      }
    })
  }

  const handleAgentWikiUpdated = (items: WikiPage[]): void => {
    setActiveStoryData((current) => {
      if (!current) return current
      const changedById = new Map(items.map((item) => [item.id, item]))
      const existingIds = new Set(current.wiki.map((item) => item.id))
      const wiki = current.wiki.map((item) => changedById.get(item.id) || item)
      for (const item of items) {
        if (!existingIds.has(item.id)) wiki.push(item)
      }
      return { ...current, wiki }
    })
  }

  const openStoryDetails = (): void => {
    if (!activeStoryData) return
    setStorySettingsSection('general')
    setStoryDetailsDraft({
      name: activeStoryData.metadata.name,
      genres: genreTokens(activeStoryData.metadata.genre),
      blurb: activeStoryData.metadata.summary || '',
      dailyGoal: activeStoryData.metadata.dailyWordGoal ?? MIN_DAILY_GOAL
    })
    setShowStoryDetails(true)
  }

  const openChapterSettings = (): void => {
    setStorySettingsSection('chapters')
  }

  // Reached from the manuscript footer's goal button, so it lands directly on the goal
  // section rather than making the writer navigate there from General.
  const openDailyGoalSettings = (): void => {
    if (!activeStoryData) return
    setStoryDetailsDraft({
      name: activeStoryData.metadata.name,
      genres: genreTokens(activeStoryData.metadata.genre),
      blurb: activeStoryData.metadata.summary || '',
      dailyGoal: activeStoryData.metadata.dailyWordGoal ?? MIN_DAILY_GOAL
    })
    setStorySettingsSection('goal')
    setShowStoryDetails(true)
  }

  const openChapterSettingsFromNavigation = (): void => {
    if (!activeStoryData) return
    setStoryDetailsDraft({
      name: activeStoryData.metadata.name,
      genres: genreTokens(activeStoryData.metadata.genre),
      blurb: activeStoryData.metadata.summary || '',
      dailyGoal: activeStoryData.metadata.dailyWordGoal ?? MIN_DAILY_GOAL
    })
    setStorySettingsSection('chapters')
    setShowStoryDetails(true)
  }

  const editChapterFromSettings = (chapterId: string): void => {
    const chapter = activeStoryData?.chapters.find((item) => item.id === chapterId)
    if (!chapter) return
    setChapterHeadingDraft({
      chapterId: chapter.id,
      title: chapter.title,
      subtitle: chapter.subtitle
    })
    setReturnToChapterSettings(true)
    setShowStoryDetails(false)
    setShowChapterHeadingDialog(true)
  }

  const closeChapterHeadingDialog = (): void => {
    setShowChapterHeadingDialog(false)
    if (returnToChapterSettings) {
      setReturnToChapterSettings(false)
      setStorySettingsSection('chapters')
      setShowStoryDetails(true)
    }
  }

  const saveStoryDetails = async (): Promise<void> => {
    if (!activeStoryData || !activeStoryId || isSavingStoryDetails) return
    const name = storyDetailsDraft.name.trim()
    if (!name) {
      showToast({ body: 'Story title is required' })
      return
    }
    const genre = Array.from(
      new Set(storyDetailsDraft.genres.map((item) => item.label.trim()).filter(Boolean))
    )
      .slice(0, 5)
      .join(', ')
    const blurb = storyDetailsDraft.blurb.trim()
    if (blurb.length > 0 && blurb.length < MIN_STORY_BLURB_LENGTH) {
      showToast({ body: `Story blurb must be at least ${MIN_STORY_BLURB_LENGTH} characters` })
      return
    }
    setIsSavingStoryDetails(true)
    try {
      await window.api.stories.save(activeStoryId, {
        metadata: { name, genre, summary: blurb }
      })
      const updatedAt = Date.now()
      setActiveStoryData((current) =>
        current
          ? {
              ...current,
              metadata: { ...current.metadata, name, genre, summary: blurb, updatedAt }
            }
          : current
      )
      setStories((current) =>
        current.map((story) =>
          story.id === activeStoryId ? { ...story, name, genre, summary: blurb, updatedAt } : story
        )
      )
      setShowStoryDetails(false)
      showToast({ body: 'Story details updated' })
    } catch (error) {
      console.error('Failed to update story details:', error)
      showToast({ body: 'Story details could not be saved' })
    } finally {
      setIsSavingStoryDetails(false)
    }
  }

  // Kept separate from saveStoryDetails: that one refuses to save until the story has a title,
  // genres, and a reader-facing blurb, and a writer setting a daily goal on a fresh draft has
  // none of those yet.
  const saveDailyGoal = async (): Promise<void> => {
    if (!activeStoryData || !activeStoryId || isSavingStoryDetails) return
    const dailyWordGoal = Math.max(MIN_DAILY_GOAL, Math.round(storyDetailsDraft.dailyGoal))
    setIsSavingStoryDetails(true)
    try {
      await window.api.stories.save(activeStoryId, { metadata: { dailyWordGoal } })
      setActiveStoryData((current) =>
        current ? { ...current, metadata: { ...current.metadata, dailyWordGoal } } : current
      )
      setStories((current) =>
        current.map((story) => (story.id === activeStoryId ? { ...story, dailyWordGoal } : story))
      )
      setShowStoryDetails(false)
      showToast({ body: `Daily goal set to ${dailyWordGoal.toLocaleString()} words` })
    } catch (error) {
      console.error('Failed to update daily goal:', error)
      showToast({ body: 'Daily goal could not be saved' })
    } finally {
      setIsSavingStoryDetails(false)
    }
  }

  // The author name is passed in rather than closed over: `publishingName` is declared far
  // below, next to the render, and reading it here would depend on closure timing.
  const handleExportPdf = async (authorName: string): Promise<void> => {
    if (!activeStoryData || isExportingPdf) return
    setIsExportingPdf(true)
    try {
      const { exportStoryPdf } = await import('./lib/manuscriptPdf')
      await exportStoryPdf({
        title: activeStoryData.metadata.name,
        author: authorName,
        chapters: sortedChapters(activeStoryData.chapters).map((chapter) => ({
          title: chapter.title,
          subtitle: chapter.subtitle,
          content: chapter.content
        }))
      })
    } catch {
      showToast({ body: "Couldn't create the PDF. Please try again.", type: 'error' })
    } finally {
      setIsExportingPdf(false)
    }
  }

  const handleDeleteActiveStory = async (): Promise<void> => {
    if (!activeStoryData || !activeStoryId || isDeletingStory) return
    const storyName = activeStoryData.metadata.name
    if (deleteStoryName !== storyName) {
      showToast({ body: 'Enter the story name exactly as shown.' })
      return
    }

    setIsDeletingStory(true)
    try {
      await window.api.stories.delete(activeStoryId)
      posthog.capture('story_deleted', { was_active_story: true })
      setStories((current) => current.filter((story) => story.id !== activeStoryId))
      setShowDeleteStoryDialog(false)
      window.location.href = '/my-stories'
    } catch (error) {
      console.error(`Failed to delete story ${activeStoryId}:`, error)
      showToast({ body: 'Unable to delete the story. Please try again.' })
    } finally {
      setIsDeletingStory(false)
    }
  }

  const generatePublicationBlurb = async (): Promise<void> => {
    if (!activeStoryId || isGeneratingBlurb) return
    if (!activeStoryData?.metadata.name.trim() || !activeStoryData.metadata.genre?.trim()) {
      showToast({ body: 'Add a story title and at least one genre before generating a blurb.' })
      return
    }
    setIsGeneratingBlurb(true)
    try {
      const result = await window.api.stories.generateBlurb(activeStoryId, {
        title: activeStoryData.metadata.name,
        genre: activeStoryData.metadata.genre || '',
        content: getFullStoryText(activeStoryData.chapters)
      })
      setPublicationBlurb(result.blurb)
    } catch (err) {
      showToast({
        body: err instanceof Error ? err.message : 'Could not generate a reader blurb.',
        type: 'error'
      })
    } finally {
      setIsGeneratingBlurb(false)
    }
  }

  const openPublishDialog = (): void => {
    setPublicationBlurb(activeStoryData?.metadata.summary?.trim() || '')
    setCoverError(null)
    setCoverUrl(null)
    setCoverStylesError(null)
    setSelectedCoverStyle(null)
    setCoverNote('')
    // Fetch while the flags are still loading too: `coverEnabled` reads false until they land, and
    // the cover route is not flag-gated, so an extra read costs nothing but a missed one leaves the
    // cover step empty for a story that has one.
    if ((coverEnabled || areFlagsLoading) && activeStoryId) {
      const storyId = activeStoryId
      void window.api.stories
        .cover(storyId)
        .then((result) => {
          if (activeStoryIdRef.current !== storyId) return
          setCoverUrl(result.coverUrl)
          setSelectedCoverStyle(result.coverStyle)
          setCoverNote(result.coverPrompt ?? '')
        })
        .catch(() => {
          if (activeStoryIdRef.current !== storyId) return
          setCoverUrl(null)
        })
      void window.api.coverStyles
        .list()
        .then((result) => {
          if (activeStoryIdRef.current !== storyId) return
          setCoverStyles(result.styles)
          // The story may still name a style an admin has since deleted. It has no tile in the
          // gallery, so leaving it selected would enable Generate with nothing visibly picked
          // (and the generate route would reject it anyway).
          setSelectedCoverStyle((current) =>
            current && !result.styles.some((style) => style.key === current) ? null : current
          )
        })
        .catch(() => {
          if (activeStoryIdRef.current !== storyId) return
          setCoverStyles([])
          setCoverStylesError('Could not load cover styles.')
        })
    }
    setShowPublishDialog(true)
  }

  const generateStoryCover = async (): Promise<void> => {
    if (!activeStoryId || isGeneratingCover) return
    const storyId = activeStoryId
    setCoverError(null)
    setIsGeneratingCover(true)
    try {
      const result = await window.api.stories.generateCover(storyId, {
        blurb: publicationBlurb.trim(),
        style: selectedCoverStyle ?? undefined,
        note: coverNote.trim() || undefined
      })
      if (activeStoryIdRef.current !== storyId) return
      setCoverUrl(result.coverUrl)
    } catch (err) {
      if (activeStoryIdRef.current !== storyId) return
      setCoverError(err instanceof Error ? err.message : 'The cover could not be generated.')
    } finally {
      setIsGeneratingCover(false)
    }
  }

  /**
   * Picks a style and asks the server to draft the art direction for it from the manuscript.
   * The style sticks even if the draft fails: the author can still write the note by hand.
   */
  const suggestCoverPrompt = async (styleKey: string): Promise<void> => {
    if (!activeStoryId || isSuggestingCoverPrompt) return
    const storyId = activeStoryId
    setSelectedCoverStyle(styleKey)
    setCoverError(null)
    setIsSuggestingCoverPrompt(true)
    try {
      const result = await window.api.stories.suggestCoverPrompt(storyId, {
        style: styleKey,
        blurb: publicationBlurb.trim() || undefined
      })
      if (activeStoryIdRef.current !== storyId) return
      setCoverNote(result.prompt)
    } catch (err) {
      if (activeStoryIdRef.current !== storyId) return
      setCoverError(err instanceof Error ? err.message : 'A prompt could not be suggested.')
    } finally {
      setIsSuggestingCoverPrompt(false)
    }
  }

  const removeStoryCover = async (): Promise<void> => {
    if (!activeStoryId) return
    const storyId = activeStoryId
    setCoverError(null)
    try {
      await window.api.stories.removeCover(storyId)
      if (activeStoryIdRef.current !== storyId) return
      setCoverUrl(null)
    } catch (err) {
      if (activeStoryIdRef.current !== storyId) return
      setCoverError(err instanceof Error ? err.message : 'The cover could not be removed.')
    }
  }

  const publishStory = async (): Promise<void> => {
    if (!activeStoryId || !activeStoryData || isPublishing) return
    if (!activeStoryData.metadata.name.trim()) {
      showToast({ body: 'Story title is required', type: 'error' })
      return
    }
    if (!activeStoryData.metadata.genre?.trim()) {
      showToast({ body: 'Choose at least one genre before publishing.', type: 'error' })
      return
    }
    if (publicationBlurb.trim().length < MIN_STORY_BLURB_LENGTH) {
      showToast({
        body: `Story blurb must be at least ${MIN_STORY_BLURB_LENGTH} characters`,
        type: 'error'
      })
      return
    }
    setIsPublishing(true)
    try {
      const result = await window.api.stories.publish(activeStoryId, {
        blurb: publicationBlurb.trim(),
        visibility: publicationVisibility,
        readers: publicationReaders
      })
      setPublication(result)
      setPublicationReaders(result.readers ?? publicationReaders)
      setActiveStoryData((current) =>
        current
          ? {
              ...current,
              metadata: { ...current.metadata, summary: publicationBlurb.trim() }
            }
          : current
      )
      setShowPublishDialog(false)
      showToast({
        body: `Published version ${result.latestVersion}.${
          result.visibility === 'unlisted'
            ? ' Only people with the link can read it.'
            : result.visibility === 'restricted'
              ? ' Only the readers you listed can read it.'
              : ''
        }`,
        type: 'info'
      })
    } catch (err) {
      showToast({
        body: err instanceof Error ? err.message : 'Story could not be published.',
        type: 'error'
      })
    } finally {
      setIsPublishing(false)
    }
  }

  const openPublishedStory = (): void => {
    if (!publication) return
    const url = new URL(publication.url, window.location.origin).toString()
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleDeleteConversation = (conversationId: string, e: React.MouseEvent): void => {
    e.stopPropagation()
    if (!activeStoryData || (activeStoryData.conversations || []).length <= 1) {
      setConfirmModal({
        show: true,
        title: 'Action Denied',
        message: 'Your story must have at least one conversation outline.',
        isAlertOnly: true,
        onConfirm: () => {}
      })
      return
    }

    setConfirmModal({
      show: true,
      title: 'Delete Conversation',
      message: 'Are you sure you want to delete this chat thread?',
      onConfirm: () => {
        const conversations = (activeStoryData.conversations || []).filter(
          (c) => c.id !== conversationId
        )
        const updated = {
          ...activeStoryData,
          conversations
        }
        void handleSaveActiveStory(updated, wikiRevisionRef.current)
        if (activeConversationId === conversationId) {
          setActiveConversationId(null)
          setShowRightPanel(false)
        }
      }
    })
  }

  const handleInsertIntoStory = (
    text: string,
    mode: 'cursor' | 'append' | 'replace' = 'cursor'
  ): void => {
    // Below the tablet threshold the conversation covers the manuscript instead of sitting
    // beside it, so prose sent to the story lands behind the chat and the writer is left facing
    // the same screen with nothing to say it arrived. Standing the overlay down costs the caret
    // nothing: the range an insert targets lives in StoryCanvas's own refs, that component stays
    // mounted underneath the overlay throughout, and the insert runs from an effect there, so
    // the panel is already gone before a single character is written. Above the threshold the
    // chat is a column alongside the manuscript, where closing it would only cost the thread.
    if (isMobile) setShowRightPanel(false)
    setInsertTextTrigger({ text, mode })
    navigateToWorkspace('canvas')
  }

  const handleStartRewriteDiscussion = async (
    selectedText: string,
    instructions: string,
    mode: 'plan' | 'ask' | 'writing' = 'writing'
  ): Promise<void> => {
    if (!activeStoryData) return

    // Only a Writing chat is asked for replacement prose; Ask and Plan chats get the
    // selection as context for a question or a scene plan instead.
    const replacementInstruction =
      mode === 'writing'
        ? `\n\nReturn each replacement option as a separate [STORY]...[/STORY] block. Do not format replacement prose as a Markdown blockquote.`
        : ''
    const promptText = `I have highlighted the following text in my story:
<SELECTED_TEXT>
${selectedText}
</SELECTED_TEXT>

My instructions:
${instructions}${replacementInstruction}`

    // The system prompt isn't added yet — it's inserted once the writer's
    // first message (this rewrite request) is sent.
    const newChat: Conversation = {
      id: createId(),
      name: 'New Chat',
      mode,
      messages: [
        {
          id: createId(),
          role: 'user',
          content: promptText,
          timestamp: Date.now(),
          metadata: {
            mode,
            selection: { text: selectedText, instructions }
          }
        }
      ]
    }
    const initialUserMessage = newChat.messages.find((message) => message.role === 'user')

    const updated = {
      ...activeStoryData,
      conversations: [...(activeStoryData?.conversations || []), newChat]
    }
    // Auto-sending this message depends on the conversation already existing server-side —
    // the /agent endpoint's ownership check 404s ("Conversation not found") otherwise. A
    // transient save failure used to be silently ignored here, so the request would fire
    // against a conversation that was never actually persisted. Retry once before giving up,
    // and only auto-send if the save actually succeeded.
    let saveResult = await handleSaveActiveStory(updated, wikiRevisionRef.current)
    if (!saveResult) {
      saveResult = await handleSaveActiveStory(updated, wikiRevisionRef.current)
    }
    const saveSucceeded = saveResult !== null
    posthog.capture('rewrite_discussion_started', {
      selected_text_length: selectedText.length,
      instruction_length: instructions.trim().length,
      mode,
      save_succeeded: saveSucceeded
    })
    setActiveConversationId(newChat.id)
    setShowRightPanel(true)
    if (!saveSucceeded) {
      showToast({
        body: "Couldn't save this rewrite request — check your connection and press send again."
      })
      return
    }
    if (initialUserMessage) {
      setPendingAgentSend({ conversationId: newChat.id, messageId: initialUserMessage.id })
    }
  }

  const handleSaveSettings = (newSettings: AppSettings): void => {
    setSettings(newSettings)
    localStorage.setItem('ninenights-settings', JSON.stringify(newSettings))
    window.api.settings.save(newSettings)
  }

  // Placed here (rather than beside the other early effects) so it comes after
  // handleCreateConversation is declared: react-hooks/immutability flags a hook body that
  // calls something declared later in the component, and does so transitively through this
  // factory's own nested closures too.
  const makeEditorTourDeps = (): TourDeps => ({
    ensureChatOpen: () => {
      void handleCreateConversation()
    },
    ensureSidebarOpen: () => setShowLeftSidebar(true),
    // The tour's last stop IS the wizard offer, so it opens the same dialog this
    // component opens for a brand-new story (line ~678) rather than a tour-only copy of
    // it. Left open when the tour ends: the writer is being handed the choice, not shown
    // a picture of it, and the dialog's own Accept/Dismiss are the two ways on.
    openWizardOffer: () => setIsWizardOfferOpen(true),
    onFinished: () => {
      activeTourControls.current = null
      // Updater form: the tour finishes minutes after it starts, and settings may
      // have changed underneath (theme, model). The save inside the updater is
      // idempotent, so a StrictMode double-invoke is harmless.
      setSettings((prev) => {
        const next = { ...prev, hasSeenEditorTour: true }
        localStorage.setItem('ninenights-settings', JSON.stringify(next))
        // Forced-destroy path (sign-out nulls activeStoryData -> the safety effect below
        // destroys the tour -> here): the session is already gone by the time this fires, so
        // the request 404s/401s. api-bridge throws on non-OK, and nothing else awaits this
        // promise -- catch it here rather than let it surface as an unhandled rejection.
        window.api.settings.save(next).catch((err) => {
          console.error('Failed to persist hasSeenEditorTour:', err)
        })
        return next
      })
    }
  })

  // Auto-start the guided tour on the first editor visit. The tour takes precedence over
  // the wizard offer modal: handleOpenStory already avoids opening the offer when the tour
  // is about to run, but settings can resolve to hasSeenEditorTour === false AFTER a story
  // has already opened (and the offer along with it) -- so this effect also closes the
  // offer defensively, right before starting the tour, to cover that late-resolving case.
  // editorTourRan also covers the manual path: "Take the tour" sets it, so a manual
  // run before settings resolve cannot be followed by an automatic rerun.
  const isTourWorkspaceReady =
    !!activeStoryData && activeStoryData.chapters.length > 0 && activeMiddleTab === 'canvas'
  useEffect(() => {
    // Five of the sixteen stops anchor inside the SideNav, and at this width AppShell has
    // moved that nav into its own drawer -- a closed <dialog>, so those anchors have no box
    // to spotlight. The tour's own escape hatch cannot reach them either: ensureSidebarOpen
    // flips this app's showLeftSidebar, which AppShell ignores below its breakpoint. Neither
    // editorTourRan nor hasSeenEditorTour is set here, so the tour is postponed to a wider
    // screen rather than spent on a walk that is a third dead.
    if (isNavCollapsed) return
    // Phones and tablets never get the tour at all -- not postponed, not degraded. The walk
    // spotlights hover-era chrome (a formatting toolbar, header actions, the sidebar) and
    // steps through it with a popover the writer is meant to click past; on a touch device
    // that is either absent or unusable. Same as the width guard above, this sets neither
    // editorTourRan nor hasSeenEditorTour, so the same account on a desktop still gets it.
    if (isHandheld) return
    if (
      !shouldAutoStartTour({
        hasSeenEditorTour: settings.hasSeenEditorTour,
        workspaceReady: isTourWorkspaceReady,
        wizardOpen: isWizardOpen,
        alreadyRanThisSession: editorTourRan.current
      })
    ) {
      return
    }
    editorTourRan.current = true
    // Defensive close of a modal that may already be on screen from handleOpenStory, right
    // before the tour (which owns the same dialog via its last stop) takes over. Not
    // derivable at render time: it depends on which of two independent call sites set
    // isWizardOfferOpen most recently.
    setIsWizardOfferOpen(false)
    activeTourControls.current = startEditorTour(makeEditorTourDeps())
    // makeEditorTourDeps intentionally excluded: it's a plain function (new identity every
    // render) whose job is to read the LATEST handleCreateConversation/setShowLeftSidebar/etc.
    // at the moment the tour actually starts, not to gate whether this effect re-runs. The
    // real trigger conditions are the four listed above -- same pattern as the eslint-disable
    // on the mount-bootstrap effect further up. isWizardOfferOpen is intentionally NOT a
    // dependency: it's only written here (via its setter, which is stable), never read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.hasSeenEditorTour, isTourWorkspaceReady, isWizardOpen, isNavCollapsed, isHandheld])

  // A running tour is not owned by React and has no unmount to hook into (see
  // activeTourControls's declaration comment above). activeStoryData is the one piece of
  // state every anchor the tour targets ultimately depends on -- the whole workspace
  // (manuscript, chat, header actions) renders conditionally on it, and sign-out is what
  // actually nulls it while WorkspaceApp itself stays mounted (its own effect swaps in the
  // landing/auth view instead). When that happens mid-tour, drive the tour's real destroy()
  // rather than let its overlay and window keyup listener strand themselves on a screen the
  // tour no longer has anything to point at -- destroy() is the same exit path Escape and
  // Close already use, so it naturally records the tour as seen like any other dismissal.
  useEffect(() => {
    if (activeStoryData) return
    activeTourControls.current?.destroy()
    activeTourControls.current = null
  }, [activeStoryData])

  const handleSaveProfile = async (changes: {
    penName: string
    authorBlurb: string
  }): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updated = await window.api.profiles.update(authorProfile.id, changes)
    setAuthorProfile(updated)
    return updated
  }

  const handleUploadAvatar = async (file: File): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updated = await window.api.profiles.uploadAvatar(authorProfile.id, file)
    setAuthorProfile(updated)
    return updated
  }

  const handleRemoveAvatar = async (): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updated = await window.api.profiles.removeAvatar(authorProfile.id)
    setAuthorProfile(updated)
    return updated
  }

  const handleSelectDefaultAvatar = async (defaultAvatarId: string): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updated = await window.api.profiles.selectDefaultAvatar(authorProfile.id, defaultAvatarId)
    setAuthorProfile(updated)
    return updated
  }

  const openSettingsSection = (section: SettingsSection): void => {
    setSettingsInitialSection(section)
    setShowSettings(true)
    setIsAccountMenuOpen(false)
    setIsThemeMenuOpen(false)
  }

  // The account menu's Profile entry goes to the writer's public profile page — the page
  // readers see — not the settings modal's profile section. Editing still lives in settings.
  const openOwnProfilePage = async (): Promise<void> => {
    setIsAccountMenuOpen(false)
    let profileId = settings.activeProfileId ?? null
    if (!profileId) {
      try {
        profileId = (await window.api.profiles.getActive())?.id ?? null
      } catch {
        profileId = null
      }
    }
    if (profileId) {
      window.location.href = `/profiles/${encodeURIComponent(profileId)}`
    } else {
      // No resolvable profile — the settings section beats a menu item that does nothing.
      openSettingsSection('profile')
    }
  }

  const selectTheme = (theme: AppSettings['theme']): void => {
    const updated = { ...settings, theme }
    setSettings(updated)
    handleSaveSettings(updated)
    applyTheme(theme)
    setIsAccountMenuOpen(false)
    setIsThemeMenuOpen(false)
  }

  const requestSignOut = (): void => {
    setIsAccountMenuOpen(false)
    setIsThemeMenuOpen(false)
    setConfirmModal({
      show: true,
      title: 'Sign out?',
      message: 'Are you sure you want to sign out of Nine Nights?',
      confirmText: 'Sign out',
      cancelText: 'Cancel',
      confirmVariant: 'primary',
      onConfirm: () => {
        void authClient
          .signOut()
          .then(() => {
            setShowAuth(false)
            posthog.reset()
          })
          .catch((error) => {
            console.error('Sign out failed:', error)
            showToast({ body: 'Unable to sign out. Please try again.', type: 'error' })
          })
      }
    })
  }

  if (isSessionPending) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          width: '100vw',
          backgroundColor: 'var(--color-bg-base)',
          color: 'var(--text-main)',
          gap: '12px'
        }}
      >
        <Loader2 className="h-6 w-6 text-primary animate-spin" />
        <p style={{ fontSize: '14px', fontWeight: 600 }}>Loading session...</p>
      </div>
    )
  }

  if (isInitializingWiki) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          width: '100vw',
          backgroundColor: 'var(--color-bg-base)',
          color: 'var(--text-main)',
          gap: '24px',
          padding: '24px'
        }}
      >
        <div
          className="thinking-dots"
          style={{
            gap: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '32px'
          }}
        >
          <span style={{ width: '10px', height: '10px' }} />
          <span style={{ width: '10px', height: '10px' }} />
          <span style={{ width: '10px', height: '10px' }} />
        </div>
        <div style={{ textAlign: 'center', maxWidth: '480px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
            Consulting the Muse...
          </h2>
          <p
            style={{
              fontSize: '14px',
              color: 'var(--text-muted)',
              minHeight: '20px',
              transition: 'all 0.3s ease'
            }}
          >
            {wikiInitStep}
          </p>
        </div>
      </div>
    )
  }

  if (!session) {
    if (!showAuth) {
      return <LandingView onEnter={() => setShowAuth(true)} />
    }
    return <AuthView onBack={() => setShowAuth(false)} />
  }

  const accountName = session.user.name?.trim() || session.user.email?.split('@')[0] || 'Account'

  const chatHistory = [...(activeStoryData?.conversations || [])]
    .filter(
      (conversation) =>
        conversation.messages &&
        conversation.messages.some(
          (message) => message.role === 'user' || message.role === 'assistant'
        )
    )
    .reverse()

  const hasManuscriptText =
    !!activeStoryData && getFullStoryText(activeStoryData.chapters).trim() !== ''
  const recentStories = [...stories].sort((a, b) => b.updatedAt - a.updatedAt)
  const publishingName = authorProfile?.penName.trim() || accountName

  // The same panel is the desktop rail and the phone drawer, so it is built once here and
  // handed to both. AppShell would otherwise auto-generate the drawer, and its MobileNav
  // picks the edge it slides from by reading document.activeElement at open time -- a tap
  // does not focus a button on iOS, so that resolution fell through to its "end" default and
  // the menu arrived from the right. Passing the drawer explicitly lets us name the side.
  //
  // Below the layout breakpoint the panel is never a rail -- AppShell only places it inline when
  // it is above its own breakpoint -- so building it costs a phone nothing on screen, while NOT
  // building it costs the drawer and the shell's top bar together: an absent sideNav clears
  // AppShell's hasSideNav, and both the drawer and the hamburger hang off that. A writer who
  // hides the rail on a desktop and narrows the window to a phone would otherwise arrive with no
  // hamburger and, now that the button no longer writes showLeftSidebar at this width, no way to
  // ask for one back.
  const workspaceSideNav =
    showLeftSidebar || isNavCollapsed ? (
      <div
        className="app-side-panel"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          borderRight: '1px solid var(--color-border)',
          overflow: 'hidden'
        }}
      >
        <SideNav
          header={
            <div
              style={
                {
                  paddingLeft: '16px',
                  paddingBottom: '16px',
                  paddingRight: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  width: '100%',
                  position: 'relative',
                  appRegion: 'drag'
                } as any
              }
            >
              <a
                href="/home"
                aria-label="Nine Nights home"
                className="workspace-home-link"
                style={
                  {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginTop: '4px',
                    appRegion: 'no-drag'
                  } as any
                }
              >
                <Wordmark size={26} tone="inverse" />
              </a>
            </div>
          }
          footer={
            <section className="account-menu-footer">
              <Popover
                isOpen={isAccountMenuOpen}
                onOpenChange={(open) => {
                  setIsAccountMenuOpen(open)
                  if (!open) setIsThemeMenuOpen(false)
                }}
                placement="above"
                alignment="start"
                width={220}
                label="Account menu"
                className="account-menu"
                content={
                  <VStack gap={2}>
                    <List density="compact">
                      <ListItem
                        label="Account settings"
                        startContent={<Settings size={16} />}
                        onClick={() => openSettingsSection('account')}
                      />
                      <ListItem
                        label="Profile"
                        startContent={<UserRound size={16} />}
                        onClick={() => void openOwnProfilePage()}
                      />
                      {/* Hidden outright on phones and tablets: the walk is a hover-era spotlight
                          over chrome a touch device either does not show or cannot drive, so the
                          offer is withdrawn rather than left to fail. Distinct from the narrow-window
                          case below, which is a desktop that just needs more room. */}
                      {!isHandheld && (
                        <ListItem
                          label="Take the tour"
                          startContent={<Compass size={16} />}
                          onClick={() => {
                            setIsAccountMenuOpen(false)
                            if (isNavCollapsed) {
                              // Several stops point at the sidebar, which the shell has
                              // folded into a drawer at this width, so the walk would spend
                              // a third of its length spotlighting nothing. Say so, rather
                              // than start a half-dead tour or do nothing at all for a menu
                              // item the writer just tapped. editorTourRan stays false: no
                              // tour ran, so nothing has been used up.
                              showToast({
                                body: 'The guided tour needs a wider window — several of its stops point at the sidebar, which is tucked away at this size.',
                                type: 'info',
                                isAutoHide: true
                              })
                              return
                            }
                            // The tour's first stop is the element-less welcome card, so
                            // it needs nothing to be mounted -- but the walk's first REAL
                            // target, the manuscript, does. Unlike the auto-start path
                            // (gated on isTourWorkspaceReady, so canvas is already mounted
                            // by the time it fires), this manual entry has to switch tabs
                            // itself -- and setActiveMiddleTab's re-render hasn't committed
                            // yet when this handler returns, so '.manuscript-surface' isn't
                            // queryable synchronously right after navigateToWorkspace. Wait
                            // for it (MANUSCRIPT_SELECTOR, not a re-typed literal that could
                            // drift from the stop it names) before driving, so the optional
                            // stops' own presence check -- run once, synchronously, inside
                            // startEditorTour -- sees the fully-mounted workspace rather
                            // than whatever was on screen before the tab switch. On
                            // timeout, start anyway -- a degraded walk beats silently doing
                            // nothing for a menu item the user just clicked; this mirrors
                            // the tradeoff buildDriveSteps's own onNextClick makes for
                            // every other stop.
                            navigateToWorkspace('canvas')
                            editorTourRan.current = true
                            void waitFor(MANUSCRIPT_SELECTOR).then(() => {
                              activeTourControls.current = startEditorTour(makeEditorTourDeps())
                            })
                          }}
                        />
                      )}
                      <ListItem
                        label={`Theme (${settings.theme.charAt(0).toUpperCase() + settings.theme.slice(1)})`}
                        startContent={<Palette size={16} />}
                        endContent={
                          isThemeMenuOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                        }
                        onClick={() => setIsThemeMenuOpen((open) => !open)}
                      />
                      {isThemeMenuOpen && (
                        <>
                          <ListItem
                            label="System"
                            startContent={<Monitor size={16} />}
                            endContent={settings.theme === 'system' ? <Check size={16} /> : null}
                            isSelected={settings.theme === 'system'}
                            className="account-theme-option"
                            onClick={() => selectTheme('system')}
                          />
                          <ListItem
                            label="Light"
                            startContent={<Sun size={16} />}
                            endContent={settings.theme === 'light' ? <Check size={16} /> : null}
                            isSelected={settings.theme === 'light'}
                            className="account-theme-option"
                            onClick={() => selectTheme('light')}
                          />
                          <ListItem
                            label="Dark"
                            startContent={<Moon size={16} />}
                            endContent={settings.theme === 'dark' ? <Check size={16} /> : null}
                            isSelected={settings.theme === 'dark'}
                            className="account-theme-option"
                            onClick={() => selectTheme('dark')}
                          />
                        </>
                      )}
                    </List>
                    <Divider />
                    <List density="compact">
                      <ListItem
                        label="Sign out"
                        startContent={<LogOut size={16} />}
                        onClick={requestSignOut}
                      />
                    </List>
                  </VStack>
                }
              >
                <PillButton
                  variant="ghost"
                  label={publishingName}
                  icon={
                    <Avatar
                      name={publishingName}
                      src={authorProfile?.avatarUrl ?? DEFAULT_AVATAR_IMAGE}
                      size="xsm"
                    />
                  }
                  className="account-menu-trigger"
                />
              </Popover>
            </section>
          }
          collapsible={{
            isCollapsed: false,
            onCollapsedChange: () => {},
            hasButton: false
          }}
        >
          <SideNavSection title="New story" isHeaderHidden>
            <SideNavItem
              label="New story"
              icon={Plus}
              onClick={handleCreateStory}
              data-testid="tour-nav-new-story"
            />
          </SideNavSection>
          {activeStoryData && activeStoryData.chapters.length > 1 && (
            <SideNavSection title="Chapters" isHeaderHidden>
              <SideNavItem
                label="Chapters"
                icon={BookOpen}
                isSelected={showStoryDetails && storySettingsSection === 'chapters'}
                onClick={openChapterSettingsFromNavigation}
                collapsible
              >
                {[...activeStoryData.chapters]
                  .sort((a, b) => a.position - b.position)
                  .map((chapter, index) => (
                    <SideNavItem
                      key={chapter.id}
                      label={`${index + 1}. ${chapter.title.trim() || `Chapter ${index + 1}`}`}
                      isSelected={activeMiddleTab === 'canvas' && activeChapterId === chapter.id}
                      onClick={() => {
                        setActiveChapterId(chapter.id)
                        navigateToWorkspace('canvas')
                      }}
                    />
                  ))}
              </SideNavItem>
            </SideNavSection>
          )}
          {activeStoryData && (
            <SideNavSection title="Story Wiki" isHeaderHidden>
              <SideNavItem
                label="Story Wiki"
                icon={BookMarked}
                isSelected={activeMiddleTab === 'wiki'}
                onClick={() => navigateToWorkspace('wiki')}
                data-testid="tour-nav-story-wiki"
              />
            </SideNavSection>
          )}
          <SideNavSection title="Recent Stories" isHeaderHidden data-tour="recent-stories">
            <SideNavItem
              label="Recent Stories"
              icon={History}
              collapsible={{ defaultIsCollapsed: false }}
            >
              {recentStories.length === 0 ? (
                <div className="text-center text-muted-foreground text-xs py-8">
                  No stories created yet.
                </div>
              ) : (
                recentStories
                  .slice(0, 5)
                  .map((s) => (
                    <SideNavItem
                      key={s.id}
                      label={s.name}
                      isSelected={activeStoryId === s.id}
                      onClick={() => handleOpenStory(s.id)}
                    />
                  ))
              )}
            </SideNavItem>
          </SideNavSection>
          <SideNavSection title="My Stories" isHeaderHidden>
            <SideNavItem
              label="My Stories"
              icon={Library}
              onClick={() => {
                window.location.href = '/my-stories'
              }}
              data-testid="tour-nav-my-stories"
            />
          </SideNavSection>
          {/* The offer dialog is shown once and then stays shut, so on a blank manuscript
                  this is the only way back to the wizard. It goes once there is prose to lose:
                  the wizard writes an opening scene, and nothing here warns about that. */}
          {activeStoryData && !hasManuscriptText && (
            <section className="wizard-nudge">
              <ClickableCard
                label="Guided Wizard"
                onClick={() => setIsWizardOpen(true)}
                padding={3}
                variant="purple"
              >
                <HStack gap={2} vAlign="center" justify="center">
                  <WandSparkles size={16} />
                  <Text type="body">Guided Wizard</Text>
                </HStack>
              </ClickableCard>
            </section>
          )}
        </SideNav>
      </div>
    ) : null

  // Below the shell's breakpoint the leading button in the workspace row has no rail to act on --
  // the nav is a drawer by then -- so it takes the shell's own top bar instead: the 49px strip
  // above the row carrying the word-mark and the hamburger. That is the only half of the 113px of
  // chrome standing above a 375px manuscript that can go. The row itself stays, because it holds
  // the chapter breadcrumb, Publish, the chat toggle and this button; hide the row and the way
  // back goes with it.
  const headerToggleIsCollapsed = isNavCollapsed ? isPhoneHeaderCollapsed : !showLeftSidebar
  const headerToggleLabel = isNavCollapsed
    ? headerToggleIsCollapsed
      ? 'Show menu bar'
      : 'Hide menu bar'
    : headerToggleIsCollapsed
      ? 'Show Sidebar'
      : 'Hide Sidebar'
  const headerToggleIcon = isNavCollapsed ? (
    headerToggleIsCollapsed ? (
      <PanelTopOpen size={14} />
    ) : (
      <PanelTopClose size={14} />
    )
  ) : headerToggleIsCollapsed ? (
    <SidebarOpen size={14} />
  ) : (
    <SidebarClose size={14} />
  )

  return (
    <AppShell
      variant="wash"
      className={
        isNavCollapsed && isPhoneHeaderCollapsed ? 'workspace-shell--header-collapsed' : undefined
      }
      sideNav={workspaceSideNav}
      mobileNav={{
        content: (
          <MobileNav
            side="start"
            className="workspace-mobile-nav"
            header={
              <a href="/home" aria-label="Nine Nights home" className="workspace-home-link">
                <Wordmark size={26} tone="inverse" />
              </a>
            }
          >
            <SideNavRenderContext value="drawer-content">{workspaceSideNav}</SideNavRenderContext>
          </MobileNav>
        )
      }}
      height="fill"
    >
      {activeStoryData ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            flex: 1,
            height: '100%',
            width: '100%',
            overflow: 'hidden',
            minWidth: 0
          }}
        >
          {isWizardOpen ? (
            <StoryOnboarding
              key={activeStoryId}
              storyId={activeStoryId ?? undefined}
              onExit={() => setIsWizardOpen(false)}
              onComplete={handleOnboardingComplete}
            />
          ) : (
            <>
              {/* Middle Column */}
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  minWidth: 0,
                  overflow: 'hidden'
                }}
              >
                <div className="flex flex-col h-full overflow-hidden">
                  {/* Workspace controls */}
                  <div
                    style={
                      {
                        padding: '10px 16px',
                        minHeight: '64px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '16px',
                        borderBottom: '1px solid var(--border-color)',
                        backgroundColor: 'var(--card-bg)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        position: 'relative',
                        appRegion: 'drag'
                      } as any
                    }
                  >
                    <HStack
                      gap={2}
                      align="center"
                      className="workspace-header-identity"
                      style={{ appRegion: 'no-drag' } as any}
                    >
                      <IconButton
                        variant="ghost"
                        size="sm"
                        icon={headerToggleIcon}
                        label={headerToggleLabel}
                        tooltip={headerToggleLabel}
                        onClick={() => {
                          if (isNavCollapsed) setIsPhoneHeaderCollapsed((collapsed) => !collapsed)
                          else setShowLeftSidebar(!showLeftSidebar)
                        }}
                      />
                      {/* The title and the chapter breadcrumb are wrapped together so a phone can
                          stack them. On a wide screen this is the row it has always been --
                          title, chevron, chapter. On a phone the two lines swap emphasis: the
                          chapter is what the writer is actually in, so it takes the readable line
                          and keeps the chevron, and the story title sits above it small and
                          uppercased as a label. Side by side there was room for neither, and the
                          title was the half that won while being the less useful of the two. */}
                      <div
                        className={`workspace-header-crumbs${
                          activeMiddleTab === 'canvas' &&
                          activeStoryData.chapters.length > 1 &&
                          activeChapter
                            ? ' has-chapter'
                            : ''
                        }`}
                      >
                        <h1 className="workspace-story-title text-lg font-bold text-foreground tracking-tight select-none">
                          {activeStoryData.metadata.name}
                        </h1>
                        {activeMiddleTab === 'canvas' &&
                          activeStoryData.chapters.length > 1 &&
                          activeChapter && (
                            <div className="workspace-header-chapter">
                              <ChevronRight
                                size={16}
                                className="text-muted-foreground shrink-0"
                                aria-hidden="true"
                              />
                              <DropdownMenu
                                button={{
                                  variant: 'ghost',
                                  size: 'sm',
                                  label:
                                    activeChapter.title.trim() ||
                                    `Chapter ${activeChapter.position + 1}`,
                                  className: 'chapter-breadcrumb-trigger'
                                }}
                                items={[...activeStoryData.chapters]
                                  .sort((a, b) => a.position - b.position)
                                  .map((chapter) => ({
                                    label:
                                      chapter.title.trim() || `Chapter ${chapter.position + 1}`,
                                    onClick: () => setActiveChapterId(chapter.id)
                                  }))}
                                menuWidth={220}
                              />
                            </div>
                          )}
                      </div>
                    </HStack>

                    <HStack
                      gap={2}
                      align="center"
                      className="workspace-header-actions"
                      style={{ appRegion: 'no-drag' } as any}
                    >
                      {/* Every story action folds behind one overflow trigger on phones,
                          Publish included. Round one kept Publish inline as the one labelled
                          button the row could afford, but it cannot afford one: at 375px the
                          row has 343px inside its padding, and 73px of Publish plus the
                          trigger, the chat toggle and their gaps leave the story title 110px
                          -- where a twenty-character title wants 167px. Without it the title
                          fits whole. Publish leads the menu, with a divider under it, so the
                          action that was a primary button still reads as the one this menu is
                          for -- and the trigger is outlined rather than ghost, because it is
                          now the only route to Publish and a borderless glyph does not look
                          like a door. It stays sized md so it stands level with the chat
                          toggle rather than at the settings icon's smaller size. Three tour
                          stops anchor on the controls this replaces, but the tour refuses to
                          run below 768px (isNavCollapsed), a range that strictly contains
                          this one, so no stop is ever left hunting for a missing node. */}
                      {isMobile ? (
                        <DropdownMenu
                          button={{
                            variant: 'ghost',
                            size: 'md',
                            label: 'Story actions',
                            icon: <MoreHorizontal size={16} />,
                            isIconOnly: true,
                            tooltip: 'Story actions',
                            className: 'workspace-header-overflow'
                          }}
                          hasChevron={false}
                          menuWidth={220}
                          items={[
                            {
                              label: 'Publish',
                              icon: <Upload size={15} />,
                              onClick: openPublishDialog
                            },
                            { type: 'divider' as const },
                            {
                              label: 'Story settings',
                              icon: <Settings size={15} />,
                              onClick: openStoryDetails
                            },
                            ...(activeMiddleTab === 'canvas'
                              ? [
                                  {
                                    label: 'New chapter',
                                    icon: <Plus size={15} />,
                                    onClick: handleAddChapter
                                  }
                                ]
                              : []),
                            ...(publication
                              ? [
                                  {
                                    label: 'Open published story',
                                    icon: <ExternalLink size={15} />,
                                    onClick: openPublishedStory
                                  }
                                ]
                              : [])
                          ]}
                        />
                      ) : (
                        <IconButton
                          variant="ghost"
                          size="sm"
                          icon={<Settings size={15} />}
                          label="Story settings"
                          tooltip="Story settings"
                          onClick={openStoryDetails}
                        />
                      )}
                      {!isMobile && activeMiddleTab === 'canvas' && (
                        <PillButton
                          variant="secondary"
                          label="New chapter"
                          onClick={handleAddChapter}
                          data-tour="new-chapter"
                        />
                      )}
                      {!isMobile && (
                        <PillButton
                          variant="primary"
                          label="Publish"
                          onClick={openPublishDialog}
                          data-tour="publish"
                        />
                      )}
                      {!isMobile && publication && (
                        <IconButton
                          variant="secondary"
                          label="Open published story"
                          tooltip="Open published story"
                          icon={<ExternalLink size={15} />}
                          onClick={openPublishedStory}
                        />
                      )}
                      <IconButton
                        variant={showRightPanel ? 'secondary' : 'primary'}
                        size="md"
                        icon={<MessageSquare size={16} />}
                        label={showRightPanel ? 'Hide Chat' : 'Show Chat'}
                        tooltip={showRightPanel ? 'Hide Chat' : 'Show Chat'}
                        onClick={() => {
                          if (showRightPanel) setShowRightPanel(false)
                          else if (activeConversationId) setShowRightPanel(true)
                          else void handleCreateConversation()
                        }}
                        className="workspace-chat-toggle"
                      />
                    </HStack>
                  </div>
                  <div className="flex-grow overflow-hidden relative flex">
                    <div className="flex-grow flex flex-col overflow-hidden">
                      <UsageQuotaBanner storyId={activeStoryId} />
                      {(() => {
                        const activeChapter =
                          activeStoryData.chapters.find((c) => c.id === activeChapterId) ??
                          activeStoryData.chapters[0]
                        if (!activeChapter) return null
                        return (
                          <StoryCanvas
                            key={activeChapter.id}
                            content={activeChapter.content}
                            totalContent={getFullStoryText(activeStoryData.chapters)}
                            hasMultipleChapters={activeStoryData.chapters.length > 1}
                            chapterTitle={
                              activeStoryData.chapters.length > 1 ? activeChapter.title : undefined
                            }
                            chapterSubtitle={activeChapter.subtitle}
                            onEditChapterHeading={
                              activeStoryData.chapters.length > 1
                                ? () => {
                                    setChapterHeadingDraft({
                                      chapterId: activeChapter.id,
                                      title: activeChapter.title,
                                      subtitle: activeChapter.subtitle
                                    })
                                    setReturnToChapterSettings(false)
                                    setShowChapterHeadingDialog(true)
                                  }
                                : undefined
                            }
                            onChangeContent={(newContent) =>
                              handleUpdateChapterContent(activeChapter.id, newContent)
                            }
                            canUndo={manuscriptUndo.canUndo}
                            canRedo={manuscriptUndo.canRedo}
                            onUndo={() => manuscriptUndo.undo(activeChapter.id)}
                            onRedo={() => manuscriptUndo.redo(activeChapter.id)}
                            onRecordEdit={(entry, kind) =>
                              manuscriptUndo.record(activeChapter.id, entry, kind)
                            }
                            onOpenHistory={() => setShowChapterHistory(true)}
                            restoredContent={restoredContent}
                            onClearRestoredContent={() => setRestoredContent(null)}
                            insertTextTrigger={insertTextTrigger}
                            onClearInsertTrigger={() => setInsertTextTrigger(null)}
                            onStartRewriteDiscussion={handleStartRewriteDiscussion}
                            onSelectionChange={setCanvasSelection}
                            onNotify={(message) => showToast({ body: message })}
                            dailyWords={
                              // A workspace left open past midnight must not keep yesterday's
                              // tally on screen; the next save re-anchors it to the new day.
                              dailyProgress && dailyProgress.day === formatLocalDate(new Date())
                                ? dailyProgress.words
                                : 0
                            }
                            dailyGoal={activeStoryData.metadata.dailyWordGoal ?? MIN_DAILY_GOAL}
                            onEditDailyGoal={openDailyGoalSettings}
                            // An orphan keeps chapter_id pointing at the chapter it was last
                            // attached to, so this one filter covers attached and orphaned notes
                            // without leaking another chapter's orphans into this margin. Memoized
                            // above (paragraphNotesForActiveChapter) — see its comment for why.
                            paragraphNotes={paragraphNotesForActiveChapter}
                            onSaveParagraphNote={(mutation, chapterHtml) =>
                              handleSaveParagraphNote(mutation, activeChapter.id, chapterHtml)
                            }
                          />
                        )
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: AI Conversation Sidebar (Collapsible & Resizable) */}
              {showRightPanel && (
                <div
                  // On a phone the chat covers the manuscript instead of sharing the row with
                  // it. The column geometry below is inline, and an inline style outranks any
                  // stylesheet rule that is not !important, so the narrow branch drops the
                  // whole object and hands the box to .chat-panel-overlay rather than fight it.
                  className={isMobile ? 'chat-panel-overlay' : undefined}
                  style={
                    isMobile
                      ? undefined
                      : {
                          width: `${rightPanelWidth}px`,
                          minWidth: 'min(280px, 50vw)',
                          maxWidth: '50vw',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative',
                          borderLeft: '1px solid var(--color-border)',
                          overflow: 'hidden',
                          flexShrink: 0
                        }
                  }
                >
                  {/* Resize Handle on the left border. A full-screen overlay has no width to
                      drag, and an invisible 8px strip down its left edge would only swallow
                      touches meant for the message beneath it, so it is not mounted there. */}
                  {!isMobile && (
                    <div
                      onMouseDown={() => setIsResizing(true)}
                      className="resize-handle"
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '8px',
                        marginLeft: '-4px',
                        cursor: 'ew-resize',
                        zIndex: 50,
                        userSelect: 'none'
                      }}
                    />
                  )}
                  {activeConversationId ? (
                    (() => {
                      const currentChat = (activeStoryData?.conversations || []).find(
                        (c) => c.id === activeConversationId
                      )
                      if (!currentChat)
                        return (
                          <div className="flex-grow flex items-center justify-center text-muted-foreground text-xs p-6 text-center">
                            Discussion not found.
                          </div>
                        )
                      return (
                        <ConversationView
                          conversation={currentChat}
                          storyId={activeStoryId!}
                          wiki={activeStoryData.wiki}
                          storyContent={getFullStoryText(activeStoryData.chapters)}
                          settings={settings}
                          onSaveSettings={handleSaveSettings}
                          onUpdateMessages={handleUpdateMessages}
                          onInsertIntoStory={handleInsertIntoStory}
                          onClose={() => setShowRightPanel(false)}
                          onNewChat={() => void handleCreateConversation()}
                          onShowHistory={() => setActiveConversationId(null)}
                          onOpenConversation={(conversationId) =>
                            setActiveConversationId(conversationId)
                          }
                          onRename={() => openConversationRename(currentChat)}
                          hasSelection={!!canvasSelection}
                          onNavigateToWiki={(_, id) => {
                            navigateToWorkspace('wiki', id)
                          }}
                          onWikiUpdated={handleAgentWikiUpdated}
                          onSetConversationMode={handleSetConversationMode}
                          onConversationTitleGenerated={handleConversationTitleGenerated}
                          autoSendMessageId={
                            pendingAgentSend?.conversationId === currentChat.id
                              ? pendingAgentSend.messageId
                              : undefined
                          }
                          onAutoSendHandled={() => setPendingAgentSend(null)}
                        />
                      )
                    })()
                  ) : (
                    <div className="flex flex-col h-full overflow-hidden">
                      {/* Discussions List Header */}
                      <div className="chat-header h-12 px-3 border-b border-border flex items-center justify-between shrink-0">
                        <h3 className="font-bold text-sm text-foreground">Chat History</h3>
                        <div className="flex items-center gap-1">
                          <IconButton
                            variant="ghost"
                            size="sm"
                            icon={<Plus size={14} />}
                            label="New Chat"
                            tooltip="New Chat"
                            onClick={() => void handleCreateConversation()}
                          />
                          <IconButton
                            variant="ghost"
                            size="sm"
                            icon={<X size={14} />}
                            label="Close Chat Panel"
                            tooltip="Close Chat Panel"
                            onClick={() => setShowRightPanel(false)}
                          />
                        </div>
                      </div>

                      {/* Discussions Scrollable List */}
                      <div className="flex-grow overflow-y-auto p-3">
                        {chatHistory.length === 0 ? (
                          <div className="h-full flex flex-col items-center justify-center text-muted-foreground text-xs text-center gap-2 py-8">
                            <MessageSquare size={20} className="text-muted mb-1" />
                            <span className="font-semibold text-foreground">
                              No Chat History Yet
                            </span>
                            <span>Start a new chat to begin working with your story agent.</span>
                          </div>
                        ) : (
                          <List hasDividers density="compact">
                            {chatHistory.map((c) => (
                              <ListItem
                                key={c.id}
                                className="group"
                                label={c.name}
                                description={formatLastActive(c)}
                                startContent={
                                  <MessageSquare
                                    size={13}
                                    className="text-muted-foreground shrink-0"
                                  />
                                }
                                endContent={
                                  <HStack gap={1}>
                                    <IconButton
                                      variant="ghost"
                                      size="sm"
                                      icon={<PenLine className="h-3 w-3" />}
                                      label="Rename Chat"
                                      tooltip="Rename Chat"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        openConversationRename(c)
                                      }}
                                      className="opacity-0 group-hover:opacity-100"
                                    />
                                    <IconButton
                                      variant="ghost"
                                      size="sm"
                                      icon={<Trash2 className="h-3 w-3" />}
                                      label="Delete Discussion"
                                      tooltip="Delete Discussion"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleDeleteConversation(c.id, e)
                                      }}
                                      className="opacity-0 group-hover:opacity-100"
                                    />
                                  </HStack>
                                }
                                onClick={() => setActiveConversationId(c.id)}
                              />
                            ))}
                          </List>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="flex-grow flex items-center justify-center h-full">
          <Spinner size="lg" label="Loading..." />
        </div>
      )}

      <Dialog
        isOpen={activeMiddleTab === 'wiki' && activeStoryData !== null}
        onOpenChange={(open) => {
          if (!open) closeWikiModal()
        }}
        width={1200}
        maxHeight="calc(100vh - 64px)"
        padding={0}
        purpose="form"
        className="wiki-dialog"
      >
        <section className="wiki-dialog-shell">
          <DialogHeader
            title="Story Wiki"
            onOpenChange={closeWikiModal}
            className="wiki-dialog-header"
          />
          <section className="wiki-dialog-content">
            {activeStoryData && (
              <WikiPanel
                key={activeStoryId}
                wiki={activeStoryData.wiki}
                onSaveWiki={handleUpdateWiki}
                storyId={activeStoryData.metadata.id}
                selectedItemId={activeWikiPageId}
                onSelectItem={(id) => {
                  if (id === activeWikiPageId) return
                  setActiveWikiPageId(id)
                  if (activeStoryId) {
                    const path = workspacePath(activeStoryId, 'wiki', id)
                    if (window.location.pathname !== path) {
                      window.history.replaceState(null, '', path)
                    }
                  }
                }}
                proposals={wikiProposals}
                onResolveProposal={handleResolveProposal}
                onSyncNow={handleSyncWikiNow}
              />
            )}
          </section>
        </section>
      </Dialog>

      {isWizardOfferOpen && activeStoryId && (
        <WizardOfferModal
          key={activeStoryId}
          onAccept={() => {
            setIsWizardOfferOpen(false)
            setIsWizardOpen(true)
          }}
          onDismiss={() => setIsWizardOfferOpen(false)}
        />
      )}

      {activeStoryId && activeChapter && (
        <ChapterHistoryDialog
          key={activeChapter.id}
          isOpen={showChapterHistory}
          storyId={activeStoryId}
          chapterId={activeChapter.id}
          currentContent={activeChapter.content}
          onClose={() => setShowChapterHistory(false)}
          onRestored={(content) => {
            setRestoredContent({ html: content, token: Date.now() })
            setShowChapterHistory(false)
            showToast({ body: 'Version restored', type: 'info', isAutoHide: true })
          }}
        />
      )}

      <Dialog
        isOpen={showStoryDetails}
        onOpenChange={setShowStoryDetails}
        width={860}
        maxHeight={isMobile ? 'calc(100dvh - 64px)' : '80vh'}
        purpose="form"
        className="story-settings-dialog"
      >
        <Layout
          start={
            <LayoutPanel width={220} padding={3} hasDivider label="Story settings navigation">
              <SettingsSectionNav
                label="Story settings navigation"
                density="spacious"
                activeId={storySettingsSection}
                sections={[
                  { id: 'general' as const, label: 'General', icon: <Settings size={16} /> },
                  ...((activeStoryData?.chapters.length || 0) >= 2
                    ? [{ id: 'chapters' as const, label: 'Chapters', icon: <BookOpen size={16} /> }]
                    : []),
                  { id: 'goal' as const, label: 'Daily goal', icon: <Target size={16} /> },
                  { id: 'export' as const, label: 'Export', icon: <Download size={16} /> },
                  { id: 'delete' as const, label: 'Delete', icon: <Trash2 size={16} /> }
                ]}
                onSelect={(id) => {
                  // Chapters is the one section that does more than switch panes -- it loads the
                  // chapter list before showing it -- so it keeps its own handler.
                  if (id === 'chapters') openChapterSettings()
                  else setStorySettingsSection(id)
                }}
              />
            </LayoutPanel>
          }
          header={
            <DialogHeader
              title={STORY_SETTINGS_COPY[storySettingsSection].title}
              subtitle={STORY_SETTINGS_COPY[storySettingsSection].subtitle}
              onOpenChange={() => setShowStoryDetails(false)}
            />
          }
          content={
            <LayoutContent>
              {storySettingsSection === 'general' ? (
                <section className="story-details-form">
                  {/* Astryx's Dialog focuses whatever declares autofocus itself, once showModal
                      has run, and on iOS that raises the software keyboard. The keyboard leaves
                      the layout viewport untouched and only pans the visual viewport onto the
                      caret, while a top-layer dialog stays centred in the layout viewport and
                      the page behind it is pinned by the dialog's scroll lock -- so the pan
                      strands the footer off screen with nothing left to drag it back. Declaring
                      nothing on a phone leaves showModal to move focus into the dialog on its
                      own, which it does per spec. */}
                  <TextInput
                    label="Story title"
                    value={storyDetailsDraft.name}
                    onChange={(name) => setStoryDetailsDraft((current) => ({ ...current, name }))}
                    placeholder="Enter a story title"
                    isRequired
                    hasAutoFocus={!isMobile}
                  />
                  <Tokenizer
                    label="Genres"
                    description="Optional. Choose up to five genres."
                    searchSource={GENRE_SEARCH_SOURCE}
                    value={storyDetailsDraft.genres}
                    onChange={(genres) =>
                      setStoryDetailsDraft((current) => ({ ...current, genres }))
                    }
                    placeholder="Search genres…"
                    renderToken={(item, onRemove) => (
                      <Token
                        key={item.id}
                        label={item.label}
                        color={item.auxiliaryData?.color}
                        size="md"
                        onRemove={onRemove}
                      />
                    )}
                    renderItem={(item) => (
                      <VStack gap={1} align="start" className="story-details-genre-option">
                        <Token label={item.label} color={item.auxiliaryData?.color} size="sm" />
                        {item.auxiliaryData?.description && (
                          <Text type="supporting">{item.auxiliaryData.description}</Text>
                        )}
                      </VStack>
                    )}
                    hasEntriesOnFocus
                    hasClear
                    maxEntries={5}
                  />
                  <StoryBlurbField
                    description={`Optional. A spoiler-free introduction shown to readers; at least ${MIN_STORY_BLURB_LENGTH} characters if provided.`}
                    value={storyDetailsDraft.blurb}
                    isRequired={false}
                    onChange={(blurb) => setStoryDetailsDraft((current) => ({ ...current, blurb }))}
                  />
                </section>
              ) : storySettingsSection === 'goal' ? (
                <Card padding={4} width="100%">
                  <VStack gap={4} align="start">
                    <NumberInput
                      label="Daily word goal"
                      description={`How many words you aim to add to this story each day. Minimum ${MIN_DAILY_GOAL.toLocaleString()}.`}
                      value={storyDetailsDraft.dailyGoal}
                      onChange={(dailyGoal) =>
                        setStoryDetailsDraft((current) => ({
                          ...current,
                          dailyGoal: dailyGoal ?? MIN_DAILY_GOAL
                        }))
                      }
                      min={MIN_DAILY_GOAL}
                      max={MAX_DAILY_GOAL}
                      step={100}
                      units="words"
                      isIntegerOnly
                      isRequired
                    />
                    <Text type="supporting">
                      Progress against this goal shows in the bar beneath your manuscript. Your
                      writing streak is separate: any day you add {MIN_DAILY_GOAL.toLocaleString()}{' '}
                      words or more across all of your stories keeps it alive.
                    </Text>
                  </VStack>
                </Card>
              ) : storySettingsSection === 'chapters' ? (
                <VStack gap={5}>
                  <ChapterSidebar
                    chapters={activeStoryData?.chapters || []}
                    activeChapterId={null}
                    onSelectChapter={editChapterFromSettings}
                    onAddChapter={handleAddChapter}
                    onDeleteChapter={(chapterId) => {
                      setShowStoryDetails(false)
                      handleDeleteChapter(chapterId)
                    }}
                    onMoveChapter={handleMoveChapter}
                    header="Arrange Chapters"
                    showAddChapter={false}
                  />
                  <Card padding={4} width="100%">
                    <VStack gap={3} align="start">
                      <VStack gap={1}>
                        <Text type="label">Merge chapters</Text>
                        <Text type="supporting">
                          Destructive action: combines every chapter into one document and
                          permanently removes chapter titles, subtitles, and ordering.
                        </Text>
                      </VStack>
                      <PillButton
                        variant="destructive"
                        label="Merge chapters"
                        onClick={() => {
                          setShowStoryDetails(false)
                          setConfirmModal({
                            show: true,
                            title: 'Merge chapters?',
                            message:
                              'This combines every chapter into a single document. Chapter titles and subtitles cannot be recovered afterward.',
                            onConfirm: handleMergeChapters
                          })
                        }}
                        isDisabled={(activeStoryData?.chapters.length || 0) <= 1}
                      />
                    </VStack>
                  </Card>
                </VStack>
              ) : storySettingsSection === 'export' ? (
                <Card padding={4} width="100%">
                  <VStack gap={4} align="start">
                    <VStack gap={1}>
                      <Text type="label">Export as PDF</Text>
                      <Text type="supporting">
                        Downloads the whole story as a PDF: a title page with your name, then every
                        chapter in the manuscript typeface, with your formatting intact.
                      </Text>
                    </VStack>
                    <PillButton
                      variant="secondary"
                      label="Download PDF"
                      icon={<Download size={16} />}
                      onClick={() => void handleExportPdf(publishingName)}
                      isLoading={isExportingPdf}
                      isDisabled={!activeStoryData}
                    />
                  </VStack>
                </Card>
              ) : (
                <Card padding={4} width="100%">
                  <VStack gap={4} align="start">
                    <VStack gap={1}>
                      <Text type="label">Delete this story</Text>
                      <Text type="supporting">
                        Warning: deleting this story permanently removes its manuscript, chapters,
                        discussions, and wiki. This action cannot be undone.
                      </Text>
                    </VStack>
                    <PillButton
                      variant="destructive"
                      label="Delete story"
                      onClick={() => {
                        setDeleteStoryName('')
                        setShowStoryDetails(false)
                        setShowDeleteStoryDialog(true)
                      }}
                    />
                  </VStack>
                </Card>
              )}
            </LayoutContent>
          }
          footer={
            <LayoutFooter>
              <HStack gap={2} hAlign="end">
                <PillButton
                  variant="secondary"
                  label="Close"
                  onClick={() => setShowStoryDetails(false)}
                  isDisabled={isSavingStoryDetails}
                />
                {storySettingsSection === 'general' && (
                  <PillButton
                    variant="primary"
                    label="Save changes"
                    onClick={() => void saveStoryDetails()}
                    isLoading={isSavingStoryDetails}
                    isDisabled={
                      !storyDetailsDraft.name.trim() ||
                      (storyDetailsDraft.blurb.trim().length > 0 &&
                        storyDetailsDraft.blurb.trim().length < MIN_STORY_BLURB_LENGTH)
                    }
                  />
                )}
                {storySettingsSection === 'goal' && (
                  <PillButton
                    variant="primary"
                    label="Save goal"
                    onClick={() => void saveDailyGoal()}
                    isLoading={isSavingStoryDetails}
                    isDisabled={storyDetailsDraft.dailyGoal < MIN_DAILY_GOAL}
                  />
                )}
              </HStack>
            </LayoutFooter>
          }
        />
      </Dialog>

      <Dialog
        isOpen={showDeleteStoryDialog}
        onOpenChange={(open) => {
          if (isDeletingStory) return
          setShowDeleteStoryDialog(open)
          if (!open) setDeleteStoryName('')
        }}
        width={460}
        className="delete-story-dialog"
        purpose="required"
      >
        <Layout
          header={
            <DialogHeader
              title="Delete story?"
              subtitle="This action cannot be undone."
              onOpenChange={() => {
                if (!isDeletingStory) {
                  setShowDeleteStoryDialog(false)
                  setDeleteStoryName('')
                }
              }}
            />
          }
          content={
            <LayoutContent>
              <VStack gap={4}>
                <Text type="supporting">
                  To confirm, type “{activeStoryData?.metadata.name || ''}” exactly as shown.
                </Text>
                <TextInput
                  label="Story name"
                  value={deleteStoryName}
                  onChange={setDeleteStoryName}
                  placeholder={activeStoryData?.metadata.name || 'Story name'}
                  hasAutoFocus
                  isRequired
                  isDisabled={isDeletingStory}
                  status={
                    deleteStoryName.length > 0 &&
                    deleteStoryName !== (activeStoryData?.metadata.name || '')
                      ? { type: 'error', message: 'The story name does not match.' }
                      : undefined
                  }
                />
              </VStack>
            </LayoutContent>
          }
          footer={
            <LayoutFooter>
              <HStack gap={2} hAlign="end">
                <PillButton
                  variant="secondary"
                  label="Cancel"
                  onClick={() => {
                    setShowDeleteStoryDialog(false)
                    setDeleteStoryName('')
                  }}
                  isDisabled={isDeletingStory}
                />
                <PillButton
                  variant="destructive"
                  label="Delete story"
                  onClick={() => void handleDeleteActiveStory()}
                  isLoading={isDeletingStory}
                  isDisabled={!activeStoryData || deleteStoryName !== activeStoryData.metadata.name}
                />
              </HStack>
            </LayoutFooter>
          }
        />
      </Dialog>

      <Dialog
        isOpen={showChapterHeadingDialog}
        onOpenChange={(open) => {
          if (!open) closeChapterHeadingDialog()
        }}
        width={420}
        purpose="form"
        className="chapter-heading-dialog"
      >
        <Layout
          header={
            <DialogHeader title="Edit chapter heading" onOpenChange={closeChapterHeadingDialog} />
          }
          content={
            <LayoutContent>
              {/* Same reason the story title field leaves this off on a phone: Astryx's Dialog
                  focuses whatever declares autofocus once showModal has run, and on iOS that
                  raises the keyboard, which pans the visual viewport onto the caret while the
                  dialog stays centred in the layout viewport -- stranding the footer off screen
                  with nothing left to drag it back. showModal moves focus into the dialog on its
                  own. */}
              <TextInput
                label="Chapter title"
                value={chapterHeadingDraft.title}
                onChange={(title) => setChapterHeadingDraft((current) => ({ ...current, title }))}
                placeholder="Chapter title"
                hasAutoFocus={!isMobile}
              />
              <TextInput
                label="Subtitle"
                value={chapterHeadingDraft.subtitle}
                onChange={(subtitle) =>
                  setChapterHeadingDraft((current) => ({ ...current, subtitle }))
                }
                placeholder="Subtitle (optional)"
              />
            </LayoutContent>
          }
          footer={
            <LayoutFooter>
              <HStack gap={2} hAlign="end">
                <PillButton
                  variant="secondary"
                  label="Cancel"
                  onClick={closeChapterHeadingDialog}
                />
                <PillButton
                  variant="primary"
                  label="Save"
                  onClick={() => {
                    handleRenameChapter(
                      chapterHeadingDraft.chapterId,
                      chapterHeadingDraft.title,
                      chapterHeadingDraft.subtitle
                    )
                    closeChapterHeadingDialog()
                  }}
                  isDisabled={!chapterHeadingDraft.title.trim()}
                />
              </HStack>
            </LayoutFooter>
          }
        />
      </Dialog>

      <PublishStoryDialog
        isOpen={showPublishDialog}
        onOpenChange={setShowPublishDialog}
        title={activeStoryData?.metadata.name ?? ''}
        genre={activeStoryData?.metadata.genre}
        genreItems={genreTokens(activeStoryData?.metadata.genre)}
        authorName={publishingName}
        publication={publication}
        blurb={publicationBlurb}
        onBlurbChange={setPublicationBlurb}
        onGenerateBlurb={() => void generatePublicationBlurb()}
        isGeneratingBlurb={isGeneratingBlurb}
        onPublish={() => void publishStory()}
        isPublishing={isPublishing}
        visibility={publicationVisibility}
        onVisibilityChange={setPublicationVisibility}
        readers={publicationReaders}
        onReadersChange={setPublicationReaders}
        coverEnabled={coverEnabled}
        coverUrl={coverUrl}
        isGeneratingCover={isGeneratingCover}
        coverError={coverError}
        onGenerateCover={() => void generateStoryCover()}
        onRemoveCover={() => void removeStoryCover()}
        onClearCoverError={() => setCoverError(null)}
        coverStyles={coverStyles}
        coverStylesError={coverStylesError}
        selectedStyle={selectedCoverStyle}
        onSelectStyle={setSelectedCoverStyle}
        onSuggestPrompt={(key) => void suggestCoverPrompt(key)}
        isSuggestingPrompt={isSuggestingCoverPrompt}
        coverNote={coverNote}
        onCoverNoteChange={setCoverNote}
      />

      {/* Global Settings Modal */}
      {showSettings && (
        <SettingsPanel
          settings={settings}
          profile={authorProfile}
          initialSection={settingsInitialSection}
          onSaveSettings={handleSaveSettings}
          onSaveProfile={handleSaveProfile}
          onUploadAvatar={handleUploadAvatar}
          onRemoveAvatar={handleRemoveAvatar}
          onSelectDefaultAvatar={handleSelectDefaultAvatar}
          onClose={() => setShowSettings(false)}
        />
      )}

      {/* Global Custom Prompt Modal */}
      <Dialog
        isOpen={promptModal.show}
        onOpenChange={(open) => setPromptModal((prev) => ({ ...prev, show: open }))}
        width={400}
        purpose="form"
        className="prompt-modal-dialog"
      >
        <Layout
          header={
            <DialogHeader
              title={promptModal.title}
              onOpenChange={() => setPromptModal((prev) => ({ ...prev, show: false }))}
            />
          }
          content={
            <LayoutContent>
              <TextInput
                label={promptModal.title}
                isLabelHidden
                placeholder={promptModal.placeholder}
                value={promptModal.value}
                onChange={(val) => setPromptModal((prev) => ({ ...prev, value: val }))}
                hasAutoFocus
                onEnter={() => {
                  promptModal.onSubmit(promptModal.value)
                  setPromptModal((prev) => ({ ...prev, show: false }))
                }}
              />
            </LayoutContent>
          }
          footer={
            <LayoutFooter>
              <HStack gap={2} hAlign="end">
                <PillButton
                  variant="secondary"
                  onClick={() => setPromptModal((prev) => ({ ...prev, show: false }))}
                  label="Cancel"
                />
                <PillButton
                  variant="primary"
                  onClick={() => {
                    promptModal.onSubmit(promptModal.value)
                    setPromptModal((prev) => ({ ...prev, show: false }))
                  }}
                  label="Confirm"
                />
              </HStack>
            </LayoutFooter>
          }
        />
      </Dialog>

      {/* Global Confirmation Modal */}
      <Dialog
        isOpen={confirmModal.show}
        onOpenChange={(open) => setConfirmModal((prev) => ({ ...prev, show: open }))}
        width={400}
        purpose="required"
        className="confirm-modal-dialog"
      >
        <Layout
          header={
            <DialogHeader
              title={confirmModal.title}
              onOpenChange={
                confirmModal.isAlertOnly
                  ? undefined
                  : () => setConfirmModal((prev) => ({ ...prev, show: false }))
              }
            />
          }
          content={
            <LayoutContent>
              <div className="py-2 text-sm text-muted-foreground">{confirmModal.message}</div>
            </LayoutContent>
          }
          footer={
            <LayoutFooter>
              <HStack gap={2} hAlign="end">
                {!confirmModal.isAlertOnly && (
                  <PillButton
                    variant="secondary"
                    onClick={() => setConfirmModal((prev) => ({ ...prev, show: false }))}
                    label={confirmModal.cancelText || 'Cancel'}
                  />
                )}
                <PillButton
                  variant={
                    confirmModal.isAlertOnly
                      ? 'primary'
                      : (confirmModal.confirmVariant ?? 'destructive')
                  }
                  onClick={() => {
                    confirmModal.onConfirm()
                    setConfirmModal((prev) => ({ ...prev, show: false }))
                  }}
                  label={confirmModal.confirmText || (confirmModal.isAlertOnly ? 'OK' : 'Delete')}
                />
              </HStack>
            </LayoutFooter>
          }
        />
      </Dialog>
    </AppShell>
  )
}

function App(): React.JSX.Element {
  // Read the session here, at the router level, from the same shared store WorkspaceApp uses.
  // The root path has to choose between the signed-out landing page and the signed-in home page
  // without mounting WorkspaceApp: mounting it starts the bootstrap that restores the writer's
  // last story, which is what used to yank them out of home a beat after it painted.
  const { data: session } = authClient.useSession()

  const publicStoryRoute = parsePublicStoryRoute()
  if (publicStoryRoute) return <PublishedStoryView {...publicStoryRoute} />
  if (window.location.pathname === '/home') return <HomeView />
  if (window.location.pathname === '/my-stories') return <AllStoriesView />
  if (window.location.pathname === '/library') return <LibraryView />
  const authorProfileRoute = parseAuthorProfileRoute()
  if (authorProfileRoute) return <AuthorProfileView {...authorProfileRoute} />
  if (window.location.pathname === '/privacy') return <PrivacyPolicyView />
  if (window.location.pathname === '/terms') return <TermsOfUseView />
  if (window.location.pathname === '/' && session?.user) {
    window.history.replaceState(null, '', '/home')
    return <HomeView />
  }
  // Signed out, or the session is still resolving: WorkspaceApp owns the landing page, the auth
  // view and the "Loading session..." screen, and its bootstrap no-ops without a session.
  return <WorkspaceApp />
}

export default App
